import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Row, ColorField } from "@/components/bi/StyleEditors";
import { FontControls, unwrapFamily } from "@/components/bi/FontStyleControls";
import { BUILTIN_STATE_MAPS, detectGeoFields } from "@/lib/geo";
import { MediaInput } from "@/components/bi/MediaInput";
import { TerritoryMatchDialog } from "@/components/bi/TerritoryMatchDialog";
import { DISPLAY_UNITS, MARKER_SHAPES, LABEL_MODES, PIE_LABEL_MODES, FUNNEL_LABEL_MODES, SCATTER_LABEL_MODES } from "@/lib/chartStyle";
import { TILE_PRESETS, TILE_KEYS } from "@/lib/mapTiles";
import { Ruler, BarChart3, Spline, PieChart, Filter as FunnelIcon, ScatterChart, Radar as RadarIcon, Gauge as GaugeIcon, LayoutGrid, Grid3x3, Palette, Map as MapIcon, MapPin, Plus, X, Tags, Upload, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function Card({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" />{title}</Label>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, placeholder, testid, className = "" }) {
  return (
    <Input data-testid={testid} type="number" value={value ?? ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      className={"bg-[color:var(--surface)] h-7 text-xs " + className} />
  );
}

function SliderRow({ label, min, max, step = 1, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px] text-[color:var(--ink-3)]">{label}</Label>
      <Slider data-testid={testid} className="mt-1.5" min={min} max={max} step={step} value={[value]} onValueChange={([v]) => onChange(v)} />
    </div>
  );
}

// Items 53/54: compact independent number format ({unit, decimals, prefix, suffix}) for one chart part
function FmtRow({ label, testid, value, onChange, hint }) {
  const f = value || {};
  const set = (patch) => onChange({ ...f, ...patch });
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-[color:var(--ink-3)]">{label}</Label>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Select value={f.unit || "default"} onValueChange={(v) => set({ unit: v === "default" ? "" : v })}>
          <SelectTrigger data-testid={`${testid}-unit`} className="bg-[color:var(--surface)] w-[128px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Measure format</SelectItem>
            {DISPLAY_UNITS.filter((u) => u.value !== "none").map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <NumInput testid={`${testid}-decimals`} value={f.decimals} placeholder="Dec." onChange={(v) => set({ decimals: v })} className="w-14" />
        <Input data-testid={`${testid}-prefix`} value={f.prefix || ""} placeholder="Prefix" onChange={(e) => set({ prefix: e.target.value })} className="bg-[color:var(--surface)] w-14 h-7 text-xs" />
        <Input data-testid={`${testid}-suffix`} value={f.suffix || ""} placeholder="Suffix" onChange={(e) => set({ suffix: e.target.value })} className="bg-[color:var(--surface)] w-14 h-7 text-xs" />
      </div>
      {hint && <p className="text-[10px] text-[color:var(--ink-3)]">{hint}</p>}
    </div>
  );
}

// ---------- Items 99/101/102/107/111/124 + axis titles + data zoom: Axis & scale ----------
export function AxisEditor({ opts, patchOpts, type }) {
  const isScatter = type === "scatter";
  const isBar = type === "bar" || type === "bar_stacked";
  const isCombo = type === "combo";
  return (
    <Card icon={Ruler} title="Axes & scale" testid="axis-editor">
      {isBar && (
        <Row label="Orientation">
          <Select value={opts.orientation || "vertical"} onValueChange={(v) => patchOpts({ orientation: v })}>
            <SelectTrigger data-testid="axis-orientation" className="bg-[color:var(--surface)] w-[120px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="vertical">Columns (↑)</SelectItem>
              <SelectItem value="horizontal">Bars (→)</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      )}
      <div className="grid grid-cols-2 gap-1.5">
        <Input data-testid="axis-x-title" value={opts.xTitle || ""} onChange={(e) => patchOpts({ xTitle: e.target.value })} placeholder="X-axis title" className="bg-[color:var(--surface)] h-7 text-xs" />
        <Input data-testid="axis-y-title" value={opts.yTitle || ""} onChange={(e) => patchOpts({ yTitle: e.target.value })} placeholder="Y-axis title" className="bg-[color:var(--surface)] h-7 text-xs" />
      </div>
      <Row label="Auto titles from field names"><Switch data-testid="axis-auto-titles" checked={!!opts.axisAutoTitles} onCheckedChange={(v) => patchOpts({ axisAutoTitles: v })} /></Row>
      <div className="grid grid-cols-3 gap-1.5">
        <NumInput testid="axis-y-min" value={opts.yMin} placeholder="Y min (auto)" onChange={(v) => patchOpts({ yMin: v })} />
        <NumInput testid="axis-y-max" value={opts.yMax} placeholder="Y max (auto)" onChange={(v) => patchOpts({ yMax: v })} />
        <NumInput testid="axis-y-interval" value={opts.yInterval} placeholder="Interval" onChange={(v) => patchOpts({ yInterval: v })} />
      </div>
      {isScatter && (
        <div className="grid grid-cols-2 gap-1.5">
          <NumInput testid="axis-x-min" value={opts.xMin} placeholder="X min (auto)" onChange={(v) => patchOpts({ xMin: v })} />
          <NumInput testid="axis-x-max" value={opts.xMax} placeholder="X max (auto)" onChange={(v) => patchOpts({ xMax: v })} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-1.5">
        <Select value={opts.axisUnit || "none"} onValueChange={(v) => patchOpts({ axisUnit: v === "none" ? "" : v })}>
          <SelectTrigger data-testid="axis-unit" className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Display units" /></SelectTrigger>
          <SelectContent>{DISPLAY_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
        </Select>
        <NumInput testid="axis-decimals" value={opts.axisDecimals} placeholder="Decimals" onChange={(v) => patchOpts({ axisDecimals: v })} />
      </div>
      {!isScatter && (
        <Row label="Label rotation (°)">
          <NumInput testid="axis-label-rotation" value={opts.labelRotation} placeholder="Auto" onChange={(v) => patchOpts({ labelRotation: v })} className="w-[80px]" />
        </Row>
      )}
      {!isScatter && (
        <Row label="Reverse category order"><Switch data-testid="axis-invert-cat" checked={!!opts.invertCat} onCheckedChange={(v) => patchOpts({ invertCat: v })} /></Row>
      )}
      <Row label="Invert value axis"><Switch data-testid="axis-invert-val" checked={!!opts.invertVal} onCheckedChange={(v) => patchOpts({ invertVal: v })} /></Row>
      <Row label={isScatter ? "Horizontal grid lines" : "Value-axis grid lines"}>
        <Switch data-testid="axis-grid-h" checked={opts.showGridH ?? opts.showGrid !== false} onCheckedChange={(v) => patchOpts({ showGridH: v })} />
      </Row>
      <Row label={isScatter ? "Vertical grid lines" : "Category-axis grid lines"}>
        <Switch data-testid="axis-grid-v" checked={opts.showGridV ?? (isScatter ? opts.showGrid !== false : false)} onCheckedChange={(v) => patchOpts({ showGridV: v })} />
      </Row>
      <Row label="Zoom / scroll (plot area)">
        <Select value={opts.dataZoom === true ? "on" : opts.dataZoom === false ? "off" : "auto"} onValueChange={(v) => patchOpts({ dataZoom: v === "on" ? true : v === "off" ? false : "auto" })}>
          <SelectTrigger data-testid="axis-datazoom" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto (dense data)</SelectItem>
            <SelectItem value="on">Always on</SelectItem>
            <SelectItem value="off">Off</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      {opts.dataZoom !== false && (
        <Row label="Categories per view">
          <NumInput testid="axis-datazoom-cats" value={opts.dataZoomCats} placeholder={(opts.orientation === "horizontal" ? 12 : 20) + ""} onChange={(v) => patchOpts({ dataZoomCats: v })} className="w-[80px]" />
        </Row>
      )}
      {isCombo && (
        <>
          <Row label="Secondary Y-axis (line)"><Switch data-testid="axis-secondary" checked={!!opts.secondaryAxis} onCheckedChange={(v) => patchOpts({ secondaryAxis: v })} /></Row>
          {opts.secondaryAxis && (
            <>
              <Input data-testid="axis-y2-title" value={opts.y2Title || ""} onChange={(e) => patchOpts({ y2Title: e.target.value })} placeholder="Y2 title" className="bg-[color:var(--surface)] h-7 text-xs" />
              <div className="grid grid-cols-3 gap-1.5">
                <NumInput testid="axis-y2-min" value={opts.y2Min} placeholder="Y2 min" onChange={(v) => patchOpts({ y2Min: v })} />
                <NumInput testid="axis-y2-max" value={opts.y2Max} placeholder="Y2 max" onChange={(v) => patchOpts({ y2Max: v })} />
                <NumInput testid="axis-y2-interval" value={opts.y2Interval} placeholder="Interval" onChange={(v) => patchOpts({ y2Interval: v })} />
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <Select value={opts.y2Unit || "none"} onValueChange={(v) => patchOpts({ y2Unit: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="axis-y2-unit" className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Units" /></SelectTrigger>
                  <SelectContent>{DISPLAY_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}

// ---------- Items 32/34/36/38/44: independent per-axis title/label/line/grid formatting ----------
export function AxisFmtEditor({ opts, patchOpts, type }) {
  const af = opts.axisFmt || {};
  const hasY2 = type === "combo" && !!opts.secondaryAxis;
  const [ax, setAx] = React.useState("x");
  const a = af[ax] || {};
  const setPart = (part, p) => patchOpts({ axisFmt: { ...af, [ax]: { ...a, [part]: { ...(a[part] || {}), ...unwrapFamily(p) } } } });
  const t = a.title || {}, l = a.labels || {}, ln = a.line || {}, g = a.grid || {};
  return (
    <Card icon={Ruler} title="Axis formatting" testid="axisfmt-editor">
      <div className="flex gap-1">
        {[["x", "X axis"], ["y", "Y axis"], ...(hasY2 ? [["y2", "Y2 (right)"]] : [])].map(([k, lab]) => (
          <button key={k} data-testid={`axisfmt-tab-${k}`} onClick={() => setAx(k)}
            className={"flex-1 h-6 rounded-md border text-[10px] " + (ax === k
              ? "bg-[color:var(--brand-ocean)] text-white border-[color:var(--brand-ocean)]"
              : "bg-[color:var(--surface)] text-[color:var(--ink-3)] border-[color:var(--glass-border)] hover:text-[color:var(--ink)]")}>
            {lab}
          </button>
        ))}
      </div>
      <Row label="Title"><Switch data-testid="axisfmt-title-show" checked={t.show !== false} onCheckedChange={(v) => setPart("title", { show: v })} /></Row>
      {t.show !== false && (
        <>
          <Input data-testid="axisfmt-title-text" value={t.text || ""} onChange={(e) => setPart("title", { text: e.target.value })}
            placeholder="Custom title (overrides Axes & scale)" className="bg-[color:var(--surface)] h-7 text-xs" />
          <FontControls testid="axisfmt-title-font" value={t} onChange={(p) => setPart("title", p)} showAlign={false} />
        </>
      )}
      <Row label="Labels"><Switch data-testid="axisfmt-label-show" checked={l.show !== false} onCheckedChange={(v) => setPart("labels", { show: v })} /></Row>
      {l.show !== false && (
        <>
          <FontControls testid="axisfmt-label-font" value={l} onChange={(p) => setPart("labels", p)} showAlign={false} />
          <Row label="Rotation (°)">
            <NumInput testid="axisfmt-label-rotation" value={l.rotation} placeholder="Auto" onChange={(v) => setPart("labels", { rotation: v })} className="w-[76px]" />
          </Row>
          <Row label="Long labels">
            <Select value={l.wrapMode || "auto"} onValueChange={(v) => setPart("labels", { wrapMode: v === "auto" ? "" : v })}>
              <SelectTrigger data-testid="axisfmt-label-wrap" className="bg-[color:var(--surface)] w-[100px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="wrap">Wrap</SelectItem>
                <SelectItem value="trim">Trim…</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          {(l.wrapMode === "wrap" || l.wrapMode === "trim") && (
            <Row label="Max label width (px)">
              <NumInput testid="axisfmt-label-maxwidth" value={l.maxWidth} placeholder="84" onChange={(v) => setPart("labels", { maxWidth: v })} className="w-[76px]" />
            </Row>
          )}
        </>
      )}
      <Row label="Axis line">
        <span className="flex items-center gap-1.5">
          <input data-testid="axisfmt-line-color" type="color" title="Line color" value={ln.color || "#c9d2de"} onChange={(e) => setPart("line", { color: e.target.value })}
            className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
          <NumInput testid="axisfmt-line-width" value={ln.width} placeholder="w" onChange={(v) => setPart("line", { width: v })} className="w-12" />
          <Switch data-testid="axisfmt-line-show" checked={ln.show !== false} onCheckedChange={(v) => setPart("line", { show: v })} />
        </span>
      </Row>
      <Row label="Grid lines">
        <span className="flex items-center gap-1.5">
          <input data-testid="axisfmt-grid-color" type="color" title="Grid color" value={g.color || "#e3e9f0"} onChange={(e) => setPart("grid", { color: e.target.value })}
            className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
          <Select value={g.style || "solid"} onValueChange={(v) => setPart("grid", { style: v })}>
            <SelectTrigger data-testid="axisfmt-grid-style" className="bg-[color:var(--surface)] w-[86px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="solid">Solid</SelectItem>
              <SelectItem value="dashed">Dashed</SelectItem>
              <SelectItem value="dotted">Dotted</SelectItem>
            </SelectContent>
          </Select>
          <Switch data-testid="axisfmt-grid-show" checked={g.show !== false} onCheckedChange={(v) => setPart("grid", { show: v })} />
        </span>
      </Row>
      <p className="text-[10px] text-[color:var(--ink-3)]">Per-axis styling on top of the Axes & scale settings — each axis is formatted independently.</p>
    </Card>
  );
}

// ---------- Items 35/39/40: per-series line/marker overrides ----------
export function SeriesStyleEditor({ config, opts, patchOpts, type }) {
  const map = opts.seriesStyle || {};
  const measureNames = (config.measures || []).map((m) => ({
    key: m.key || m.alias || `${m.agg || "sum"}_${m.field}`,
    label: m.label || m.key || `${m.agg || "sum"} ${m.field}`,
  }));
  const [pick, setPick] = React.useState("");
  const names = Object.keys(map);
  const setSeries = (n, patch) => patchOpts({ seriesStyle: { ...map, [n]: { ...(map[n] || {}), ...unwrapFamily(patch) } } });
  const removeSeries = (n) => { const next = { ...map }; delete next[n]; patchOpts({ seriesStyle: next }); };
  const addable = measureNames.filter((m) => !map[m.key]);
  return (
    <Card icon={Spline} title="Per-series overrides" testid="seriesstyle-editor">
      <div className="flex gap-1.5">
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger data-testid="seriesstyle-pick" className="bg-[color:var(--surface)] flex-1 h-7 text-xs"><SelectValue placeholder="Pick a series…" /></SelectTrigger>
          <SelectContent>
            {addable.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="seriesstyle-add" size="sm" variant="outline" className="h-7 text-[10px] px-2" disabled={!pick}
          onClick={() => { if (pick) { setSeries(pick, {}); setPick(""); } }}>
          Add
        </Button>
      </div>
      {!names.length && <p className="text-[10px] text-[color:var(--ink-3)]">Give any series its own color, line width, dash, markers and smoothing — independent of the chart defaults.</p>}
      {names.map((n) => {
        const st = map[n] || {};
        const label = measureNames.find((m) => m.key === n)?.label || n;
        return (
          <div key={n} data-testid={`seriesstyle-entry-${n}`} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-[color:var(--ink-2)] truncate">{label}</span>
              <button onClick={() => removeSeries(n)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
            </div>
            <Row label="Color / width / dash">
              <span className="flex items-center gap-1.5">
                <input data-testid={`seriesstyle-color-${n}`} type="color" value={st.color || "#2B4EFF"} onChange={(e) => setSeries(n, { color: e.target.value })} className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
                <NumInput testid={`seriesstyle-width-${n}`} value={st.width} placeholder="w" onChange={(v) => setSeries(n, { width: v })} className="w-11" />
                <Select value={st.dash || "solid"} onValueChange={(v) => setSeries(n, { dash: v })}>
                  <SelectTrigger data-testid={`seriesstyle-dash-${n}`} className="bg-[color:var(--surface-2)] w-[76px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solid">Solid</SelectItem>
                    <SelectItem value="dashed">Dashed</SelectItem>
                    <SelectItem value="dotted">Dotted</SelectItem>
                  </SelectContent>
                </Select>
              </span>
            </Row>
            <Row label="Marker">
              <span className="flex items-center gap-1.5">
                <Select value={st.marker || "inherit"} onValueChange={(v) => setSeries(n, { marker: v === "inherit" ? "" : v })}>
                  <SelectTrigger data-testid={`seriesstyle-marker-${n}`} className="bg-[color:var(--surface-2)] w-[100px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Inherit</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                    {MARKER_SHAPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <NumInput testid={`seriesstyle-marker-size-${n}`} value={st.markerSize} placeholder="sz" onChange={(v) => setSeries(n, { markerSize: v })} className="w-11" />
              </span>
            </Row>
            <Row label="Smooth line">
              <Switch data-testid={`seriesstyle-smooth-${n}`} checked={st.smooth === true} onCheckedChange={(v) => setSeries(n, { smooth: v })} />
            </Row>
            {type === "area" && (
              <Row label="Fill opacity">
                <NumInput testid={`seriesstyle-area-opacity-${n}`} value={st.areaOpacity} placeholder="0.2" onChange={(v) => setSeries(n, { areaOpacity: v })} className="w-14" />
              </Row>
            )}
          </div>
        );
      })}
    </Card>
  );
}

// ---------- Item 103 + item 7: data label content, position & independent typography ----------
export function DataLabelEditor({ opts, patchOpts }) {
  if (!opts.showLabels) return null;
  const dl = opts.dataLabel || {};
  const setDl = (patch) => patchOpts({ dataLabel: { ...dl, ...patch } });
  return (
    <Card icon={Tags} title="Data labels" testid="datalabel-editor">
      <Row label="Content">
        <Select value={opts.labelMode || "value"} onValueChange={(v) => patchOpts({ labelMode: v })}>
          <SelectTrigger data-testid="label-mode" className="bg-[color:var(--surface)] w-[160px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{LABEL_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <Row label="Position">
        <Select value={opts.labelPosition || "auto"} onValueChange={(v) => patchOpts({ labelPosition: v === "auto" ? "" : v })}>
          <SelectTrigger data-testid="label-position" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto</SelectItem>
            <SelectItem value="top">Top</SelectItem>
            <SelectItem value="inside">Inside</SelectItem>
            <SelectItem value="right">Right</SelectItem>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="bottom">Bottom</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Rotation">
        <Select value={String(dl.rotate ?? 0)} onValueChange={(v) => setDl({ rotate: Number(v) })}>
          <SelectTrigger data-testid="label-rotate" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">0°</SelectItem>
            <SelectItem value="45">45°</SelectItem>
            <SelectItem value="-45">-45°</SelectItem>
            <SelectItem value="90">90°</SelectItem>
            <SelectItem value="-90">-90°</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Text style">
        <span className="flex items-center gap-1.5">
          <NumInput testid="label-font-size" value={dl.fontSize} placeholder="10" onChange={(v) => setDl({ fontSize: v })} className="w-14" />
          <ColorField testid="label-color" value={dl.color} fallback="#475569" onChange={(v) => setDl({ color: v })} />
          <Button data-testid="label-bold" type="button" variant={dl.bold ? "default" : "outline"} size="sm" className="h-7 px-2 text-xs" onClick={() => setDl({ bold: !dl.bold })}>B</Button>
          <Button data-testid="label-italic" type="button" variant={dl.italic ? "default" : "outline"} size="sm" className="h-7 px-2 text-xs italic" onClick={() => setDl({ italic: !dl.italic })}>I</Button>
        </span>
      </Row>
      <Row label="Hide overlapping labels"><Switch data-testid="label-hide-overlap" checked={dl.hideOverlap !== false} onCheckedChange={(v) => setDl({ hideOverlap: v })} /></Row>
      <p className="text-[10px] text-[color:var(--ink-3)]">Label font, colour and rotation are independent of axis, legend and title styling.</p>
    </Card>
  );
}

// ---------- Item 22: combo (bar + line) data labels configured independently per family ----------
const COMBO_LABEL_POS = {
  bar: [["top", "Above bar"], ["inside", "Inside"], ["insideTop", "Inside top"], ["insideBottom", "Inside bottom"], ["bottom", "Below"]],
  line: [["top", "Above point"], ["bottom", "Below point"], ["left", "Left"], ["right", "Right"], ["inside", "On point"]],
};
function ComboLabelBlock({ fam, title, cfg, set, inherited }) {
  const show = cfg.show === undefined ? inherited : !!cfg.show;
  return (
    <div data-testid={`combo-label-${fam}`} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1">
      <Row label={title}><Switch data-testid={`combo-label-${fam}-show`} checked={show} onCheckedChange={(v) => set({ show: v })} /></Row>
      {show && (
        <>
          <Row label="Content">
            <Select value={cfg.mode || "inherit"} onValueChange={(v) => set({ mode: v === "inherit" ? "" : v })}>
              <SelectTrigger data-testid={`combo-label-${fam}-mode`} className="bg-[color:var(--surface-2)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">Same as chart</SelectItem>
                {LABEL_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Row>
          <Row label="Position">
            <Select value={cfg.position || "auto"} onValueChange={(v) => set({ position: v === "auto" ? "" : v })}>
              <SelectTrigger data-testid={`combo-label-${fam}-position`} className="bg-[color:var(--surface-2)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto</SelectItem>
                {COMBO_LABEL_POS[fam].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </Row>
          <Row label="Rotation">
            <Select value={String(cfg.rotate ?? 0)} onValueChange={(v) => set({ rotate: Number(v) })}>
              <SelectTrigger data-testid={`combo-label-${fam}-rotate`} className="bg-[color:var(--surface-2)] w-[90px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{[0, 45, -45, 90, -90].map((r) => <SelectItem key={r} value={String(r)}>{r}°</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          <Row label="Text style">
            <span className="flex items-center gap-1.5">
              <NumInput testid={`combo-label-${fam}-size`} value={cfg.fontSize} placeholder="10" onChange={(v) => set({ fontSize: v })} className="w-14" />
              <ColorField testid={`combo-label-${fam}-color`} value={cfg.color} fallback="#475569" onChange={(v) => set({ color: v })} />
              <Button data-testid={`combo-label-${fam}-bold`} type="button" variant={cfg.bold ? "default" : "outline"} size="sm" className="h-7 px-2 text-xs" onClick={() => set({ bold: !cfg.bold })}>B</Button>
              <Button data-testid={`combo-label-${fam}-italic`} type="button" variant={cfg.italic ? "default" : "outline"} size="sm" className="h-7 px-2 text-xs italic" onClick={() => set({ italic: !cfg.italic })}>I</Button>
            </span>
          </Row>
          <Row label="Gap from shape">
            <NumInput testid={`combo-label-${fam}-distance`} value={cfg.distance} placeholder={fam === "line" ? "8" : "4"} onChange={(v) => set({ distance: v })} className="w-[70px]" />
          </Row>
          <Row label="Shift labels to avoid overlap">
            <Switch data-testid={`combo-label-${fam}-move`} checked={cfg.moveOverlap !== "off"} onCheckedChange={(v) => set({ moveOverlap: v ? "" : "off" })} />
          </Row>
          <Row label="Hide labels that still clash">
            <Switch data-testid={`combo-label-${fam}-hide-overlap`} checked={cfg.hideOverlap !== false} onCheckedChange={(v) => set({ hideOverlap: v })} />
          </Row>
        </>
      )}
    </div>
  );
}

export function ComboLabelEditor({ opts, patchOpts }) {
  const cl = opts.comboLabels || {};
  const set = (fam) => (patch) => patchOpts({ comboLabels: { ...cl, [fam]: { ...(cl[fam] || {}), ...patch } } });
  return (
    <Card icon={Tags} title="Data labels — bars & lines" testid="combo-label-editor">
      <ComboLabelBlock fam="bar" title="Column / bar labels" cfg={cl.bar || {}} set={set("bar")} inherited={!!opts.showLabels} />
      <ComboLabelBlock fam="line" title="Line / point labels" cfg={cl.line || {}} set={set("line")} inherited={!!opts.showLabels} />
      <p className="text-[10px] text-[color:var(--ink-3)]">Bar and line labels are fully independent. Secondary-axis series use the secondary display unit automatically.</p>
    </Card>
  );
}

// ---------- Items 104/105/106/108: bar styling ----------
export function BarStyleEditor({ opts, patchOpts, type }) {
  const pn = opts.posNeg || {};
  const setPn = (patch) => patchOpts({ posNeg: { ...pn, ...patch } });
  return (
    <Card icon={BarChart3} title="Bar style" testid="barstyle-editor">
      <Row label="Bar / column colour">
        <span className="flex items-center gap-1.5">
          <ColorField testid="bar-color" value={opts.barColor} fallback="" onChange={(v) => patchOpts({ barColor: v })} />
          {opts.barColor && <Button data-testid="bar-color-clear" type="button" variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={() => patchOpts({ barColor: "" })}>Theme</Button>}
        </span>
      </Row>
      <p className="text-[10px] text-[color:var(--ink-3)]">Colour priority: conditional / per-category colour → this colour → theme palette.</p>
      <SliderRow label={`Bar width (${opts.barWidth ? opts.barWidth + "%" : "auto"})`} min={0} max={100} step={5} value={Number(opts.barWidth) || 0} onChange={(v) => patchOpts({ barWidth: v || "" })} testid="bar-width" />
      <SliderRow label={`Category spacing (${opts.barCatGap ?? 20}%)`} min={0} max={80} step={5} value={Number(opts.barCatGap ?? 20)} onChange={(v) => patchOpts({ barCatGap: v })} testid="bar-catgap" />
      <SliderRow label={`Corner radius (${opts.barRadius ?? 10}px)`} min={0} max={24} step={1} value={Number(opts.barRadius ?? 10)} onChange={(v) => patchOpts({ barRadius: v })} testid="bar-radius" />
      <Row label="Positive / negative colors"><Switch data-testid="bar-posneg" checked={!!pn.enabled} onCheckedChange={(v) => setPn({ enabled: v })} /></Row>
      {pn.enabled && (
        <div className="flex items-center gap-2">
          <NumInput testid="bar-posneg-threshold" value={pn.threshold ?? 0} placeholder="Threshold" onChange={(v) => setPn({ threshold: v === "" ? 0 : v })} className="flex-1" />
          <ColorField testid="bar-pos-color" value={pn.pos} fallback="#16A34A" onChange={(v) => setPn({ pos: v })} />
          <ColorField testid="bar-neg-color" value={pn.neg} fallback="#DC2626" onChange={(v) => setPn({ neg: v })} />
        </div>
      )}
      <Row label="Heat colors by value"><Switch data-testid="bar-heat" checked={!!(opts.barHeat || {}).enabled} onCheckedChange={(v) => patchOpts({ barHeat: { ...(opts.barHeat || {}), enabled: v } })} /></Row>
      {(opts.barHeat || {}).enabled && (
        <Row label="Low → High">
          <span className="flex items-center gap-1.5">
            <input data-testid="bar-heat-low" type="color" value={(opts.barHeat || {}).low || "#BFDBFE"} onChange={(e) => patchOpts({ barHeat: { ...(opts.barHeat || {}), low: e.target.value } })} className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
            <input data-testid="bar-heat-high" type="color" value={(opts.barHeat || {}).high || "#1D4ED8"} onChange={(e) => patchOpts({ barHeat: { ...(opts.barHeat || {}), high: e.target.value } })} className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
          </span>
        </Row>
      )}
      {opts.gradient !== false && (
        <>
          <Row label="Gradient direction">
            <Select value={opts.gradientDir || "vertical"} onValueChange={(v) => patchOpts({ gradientDir: v })}>
              <SelectTrigger data-testid="bar-gradient-dir" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vertical">Vertical</SelectItem>
                <SelectItem value="horizontal">Horizontal</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <SliderRow label={`Gradient fade (${Math.round((opts.gradientOpacity ?? 0.33) * 100)}%)`} min={0} max={100} step={5} value={Math.round((opts.gradientOpacity ?? 0.33) * 100)} onChange={(v) => patchOpts({ gradientOpacity: v / 100 })} testid="bar-gradient-opacity" />
        </>
      )}
      {type === "bar" && (
        <>
          <div>
            <Label className="text-[11px] text-[color:var(--ink-3)]">Infographic icon / image (replaces bars)</Label>
            <div className="mt-1"><MediaInput testid="bar-image-url" value={opts.barImageUrl || ""} onChange={(v) => patchOpts({ barImageUrl: v })} placeholder="https://.../icon.png" /></div>
          </div>
          {opts.barImageUrl && (
            <>
              <Row label="Icon mode">
                <Select value={opts.barImageMode || "repeat"} onValueChange={(v) => patchOpts({ barImageMode: v })}>
                  <SelectTrigger data-testid="bar-image-mode" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="repeat">Repeat</SelectItem>
                    <SelectItem value="stretch">Stretch</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
              {(opts.barImageMode || "repeat") === "repeat" && (
                <SliderRow label={`Icon size (${opts.barImageSize || 24}px)`} min={10} max={64} step={2} value={Number(opts.barImageSize) || 24} onChange={(v) => patchOpts({ barImageSize: v })} testid="bar-image-size" />
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
}

// ---------- Items 109/110/113/114/115: line & area styling + markers ----------
export function LineStyleEditor({ opts, patchOpts, type }) {
  const showMarkers = typeof opts.showMarkers === "boolean" ? opts.showMarkers : true;
  return (
    <Card icon={Spline} title={type === "area" ? "Area & line style" : "Line style"} testid="linestyle-editor">
      <SliderRow label={`Line width (${opts.lineWidth || 2.5}px)`} min={1} max={8} step={0.5} value={Number(opts.lineWidth) || 2.5} onChange={(v) => patchOpts({ lineWidth: v })} testid="line-width" />
      <Row label="Line style">
        <Select value={opts.lineDash || "solid"} onValueChange={(v) => patchOpts({ lineDash: v })}>
          <SelectTrigger data-testid="line-dash" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="solid">Solid</SelectItem>
            <SelectItem value="dashed">Dashed</SelectItem>
            <SelectItem value="dotted">Dotted</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Show markers"><Switch data-testid="line-markers" checked={showMarkers} onCheckedChange={(v) => patchOpts({ showMarkers: v })} /></Row>
      <Row label="Missing values">
        <Select value={opts.nullMode || "gap"} onValueChange={(v) => patchOpts({ nullMode: v })}>
          <SelectTrigger data-testid="line-null-mode" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="gap">Show gap</SelectItem>
            <SelectItem value="connect">Connect points</SelectItem>
            <SelectItem value="zero">Treat as zero</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      {showMarkers && (
        <>
          <Row label="Marker shape">
            <Select value={opts.markerShape || "circle"} onValueChange={(v) => patchOpts({ markerShape: v })}>
              <SelectTrigger data-testid="line-marker-shape" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{MARKER_SHAPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          <SliderRow label={`Marker size (${opts.markerSize || 7}px)`} min={2} max={20} step={1} value={Number(opts.markerSize) || 7} onChange={(v) => patchOpts({ markerSize: v })} testid="line-marker-size" />
          <Row label="Marker color"><ColorField testid="line-marker-color" value={opts.markerColor} fallback="#2B4EFF" onChange={(v) => patchOpts({ markerColor: v })} /></Row>
        </>
      )}
      {type === "area" && (
        <>
          <SliderRow label={`Fill opacity (${Math.round((opts.areaOpacity ?? 0.2) * 100)}%)`} min={5} max={100} step={5} value={Math.round((opts.areaOpacity ?? 0.2) * 100)} onChange={(v) => patchOpts({ areaOpacity: v / 100 })} testid="area-opacity" />
          <Row label="Gradient fill (fade down)"><Switch data-testid="area-gradient" checked={!!opts.areaGradient} onCheckedChange={(v) => patchOpts({ areaGradient: v })} /></Row>
        </>
      )}
    </Card>
  );
}

// ---------- Items 116/117: pie & donut labels + sorting ----------
export function PieStyleEditor({ opts, patchOpts }) {
  const numOr = (v, d) => (v === undefined || v === null || v === "" ? d : Number(v));
  return (
    <Card icon={PieChart} title="Slices, labels & center" testid="piestyle-editor">
      <Row label="Label content">
        <Select value={opts.pieLabelMode || "cat_pct"} onValueChange={(v) => patchOpts({ pieLabelMode: v })}>
          <SelectTrigger data-testid="pie-label-mode" className="bg-[color:var(--surface)] w-[160px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{PIE_LABEL_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <Row label="Label position">
        <Select value={opts.pieLabelPos || "outside"} onValueChange={(v) => patchOpts({ pieLabelPos: v })}>
          <SelectTrigger data-testid="pie-label-pos" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="outside">Outside</SelectItem>
            <SelectItem value="inside">Inside</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label={`Label size (${opts.pieLabelFontSize || 12}px)`}>
        <div className="w-[110px]"><Slider data-testid="pie-label-size" min={8} max={22} step={1} value={[opts.pieLabelFontSize || 12]} onValueChange={([v]) => patchOpts({ pieLabelFontSize: v })} /></div>
      </Row>
      <Row label="Label color"><ColorField testid="pie-label-color" value={opts.pieLabelColor} fallback="#51607A" onChange={(v) => patchOpts({ pieLabelColor: v })} /></Row>
      <Row label="Slice sorting">
        <Select value={opts.pieSort || "none"} onValueChange={(v) => patchOpts({ pieSort: v })}>
          <SelectTrigger data-testid="pie-sort" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">As queried</SelectItem>
            <SelectItem value="desc">Largest first</SelectItem>
            <SelectItem value="asc">Smallest first</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label={`Outer radius (${numOr(opts.pieOuterRadius, opts.semiDonut ? 95 : 72)}%)`}>
        <div className="w-[110px]"><Slider data-testid="pie-outer-radius" min={40} max={98} step={2} value={[numOr(opts.pieOuterRadius, opts.semiDonut ? 95 : 72)]} onValueChange={([v]) => patchOpts({ pieOuterRadius: v })} /></div>
      </Row>
      <Row label={`Slice border (${numOr(opts.pieBorderWidth, 2)}px)`}>
        <div className="w-[110px]"><Slider data-testid="pie-border-width" min={0} max={8} step={1} value={[numOr(opts.pieBorderWidth, 2)]} onValueChange={([v]) => patchOpts({ pieBorderWidth: v })} /></div>
      </Row>
      <Row label="Border color"><ColorField testid="pie-border-color" value={opts.pieBorderColor} fallback="#ffffff" onChange={(v) => patchOpts({ pieBorderColor: v })} /></Row>
      <Row label={`Corner radius (${numOr(opts.pieBorderRadius, 8)}px)`}>
        <div className="w-[110px]"><Slider data-testid="pie-corner-radius" min={0} max={20} step={1} value={[numOr(opts.pieBorderRadius, 8)]} onValueChange={([v]) => patchOpts({ pieBorderRadius: v })} /></div>
      </Row>
      <Row label={`Slice spacing (${numOr(opts.piePadAngle, 0)}°)`}>
        <div className="w-[110px]"><Slider data-testid="pie-pad-angle" min={0} max={10} step={1} value={[numOr(opts.piePadAngle, 0)]} onValueChange={([v]) => patchOpts({ piePadAngle: v })} /></div>
      </Row>
      {!opts.semiDonut && (
        <Row label="Start angle (°)">
          <Input data-testid="pie-start-angle" type="number" value={opts.pieStartAngle ?? ""} placeholder="90"
            onChange={(e) => patchOpts({ pieStartAngle: e.target.value })} className="bg-[color:var(--surface)] w-[90px] h-7 text-xs" />
        </Row>
      )}
      {!opts.semiDonut && opts.pieStartAngle !== undefined && opts.pieStartAngle !== "" && (
        <Row label="End angle (°)">
          <Input data-testid="pie-end-angle" type="number" value={opts.pieEndAngle ?? ""} placeholder="auto"
            onChange={(e) => patchOpts({ pieEndAngle: e.target.value })} className="bg-[color:var(--surface)] w-[90px] h-7 text-xs" />
        </Row>
      )}
      <Row label="Explode slice">
        <Input data-testid="pie-explode-slice" value={opts.pieExplodeSlice || ""} placeholder="Slice name"
          onChange={(e) => patchOpts({ pieExplodeSlice: e.target.value })} className="bg-[color:var(--surface)] w-[130px] h-7 text-xs" />
      </Row>
      <Row label="Slice shadow"><Switch data-testid="pie-shadow" checked={!!opts.pieShadow} onCheckedChange={(v) => patchOpts({ pieShadow: v })} /></Row>
      <Row label="Center content">
        <Select value={opts.pieCenterMode || "none"} onValueChange={(v) => patchOpts({ pieCenterMode: v === "none" ? "" : v })}>
          <SelectTrigger data-testid="pie-center-mode" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="total">Total value</SelectItem>
            <SelectItem value="custom">Custom text</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      {opts.pieCenterMode === "custom" && (
        <Input data-testid="pie-center-text" value={opts.pieCenterText || ""} placeholder="Center text" onChange={(e) => patchOpts({ pieCenterText: e.target.value })} className="bg-[color:var(--surface)] h-7 text-xs" />
      )}
      {opts.pieCenterMode === "total" && (
        <Input data-testid="pie-center-label" value={opts.pieCenterLabel || ""} placeholder="Caption (default: Total)" onChange={(e) => patchOpts({ pieCenterLabel: e.target.value })} className="bg-[color:var(--surface)] h-7 text-xs" />
      )}
      {opts.pieCenterMode && opts.pieCenterMode !== "none" && (
        <Row label={`Center size (${opts.pieCenterFontSize || 22}px)`}>
          <div className="w-[110px]"><Slider data-testid="pie-center-size" min={12} max={44} step={1} value={[opts.pieCenterFontSize || 22]} onValueChange={([v]) => patchOpts({ pieCenterFontSize: v })} /></div>
        </Row>
      )}
    </Card>
  );
}

// ---------- Item 15: combo per-series type / axis / color ----------
export function ComboSeriesEditor({ config, opts, patchOpts }) {
  const measures = config?.measures || [];
  const keys = measures.map((m) => m.key || m.alias || `${m.agg || "sum"}_${m.field}`);
  const cs = opts.comboSeries || {};
  const upd = (k, patch) => patchOpts({ comboSeries: { ...cs, [k]: { ...(cs[k] || {}), ...patch } } });
  if (keys.length < 1) return null;
  return (
    <Card icon={BarChart3} title="Series setup (bar / line & axis)" testid="combo-series-editor">
      {keys.map((k, idx) => {
        const c = cs[k] || {};
        const defLine = idx >= keys.length - Math.max(1, Math.min(keys.length - 1 || 1, Number(opts.comboLines) || 1));
        return (
          <div key={k} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1">
            <div className="text-[11px] font-medium text-[color:var(--ink-2)] truncate">{k}</div>
            <div className="flex gap-1 items-center">
              <Select value={c.type || (defLine ? "line" : "bar")} onValueChange={(v) => upd(k, { type: v })}>
                <SelectTrigger data-testid={`combo-type-${idx}`} className="bg-[color:var(--surface-2)] flex-1 h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Column</SelectItem>
                  <SelectItem value="line">Line</SelectItem>
                  <SelectItem value="spline">Spline line</SelectItem>
                  <SelectItem value="step">Step line</SelectItem>
                  <SelectItem value="area">Area</SelectItem>
                  <SelectItem value="spline_area">Spline area</SelectItem>
                  <SelectItem value="scatter">Scatter</SelectItem>
                  <SelectItem value="bubble">Bubble</SelectItem>
                  <SelectItem value="dot">Dot plot</SelectItem>
                  <SelectItem value="lollipop">Lollipop</SelectItem>
                  <SelectItem value="range_column">Range column</SelectItem>
                </SelectContent>
              </Select>
              <Select value={c.axis || "y"} onValueChange={(v) => upd(k, { axis: v })}>
                <SelectTrigger data-testid={`combo-axis-${idx}`} className="bg-[color:var(--surface-2)] flex-1 h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="y">Primary axis</SelectItem>
                  <SelectItem value="y2">Secondary axis</SelectItem>
                </SelectContent>
              </Select>
              <input data-testid={`combo-color-${idx}`} type="color" value={c.color || "#2B4EFF"} onChange={(e) => upd(k, { color: e.target.value })}
                className="h-7 w-8 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />
              {c.color && <button onClick={() => upd(k, { color: "" })} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><X className="w-3 h-3" /></button>}
            </div>
            {c.type === "range_column" && (
              <div className="flex gap-1 items-center">
                <span className="text-[10px] text-[color:var(--ink-3)] shrink-0">Low bound</span>
                <Select value={c.rangeLow || ""} onValueChange={(v) => upd(k, { rangeLow: v })}>
                  <SelectTrigger data-testid={`combo-rangelow-${idx}`} className="bg-[color:var(--surface-2)] flex-1 h-6 text-[10px]"><SelectValue placeholder="Pick low measure" /></SelectTrigger>
                  <SelectContent>{keys.filter((kk) => kk !== k).map((kk) => <SelectItem key={kk} value={kk}>{kk}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-1 items-center flex-wrap">
              <Select value={c.marker || "auto"} onValueChange={(v) => upd(k, { marker: v === "auto" ? "" : v })}>
                <SelectTrigger data-testid={`combo-marker-${idx}`} className="bg-[color:var(--surface-2)] w-[92px] h-6 text-[10px]"><SelectValue placeholder="Marker" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Marker: auto</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="circle">Circle</SelectItem>
                  <SelectItem value="rect">Square</SelectItem>
                  <SelectItem value="triangle">Triangle</SelectItem>
                  <SelectItem value="diamond">Diamond</SelectItem>
                  <SelectItem value="pin">Pin</SelectItem>
                  <SelectItem value="arrow">Arrow</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid={`combo-marker-size-${idx}`} type="number" min={2} max={40} value={c.markerSize ?? ""} placeholder="sz"
                onChange={(e) => upd(k, { markerSize: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] h-6 text-[10px] w-[46px]" />
              <Input data-testid={`combo-width-${idx}`} type="number" min={1} max={12} value={c.width ?? ""} placeholder="w"
                onChange={(e) => upd(k, { width: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] h-6 text-[10px] w-[42px]" />
              <Select value={c.dash || "solid"} onValueChange={(v) => upd(k, { dash: v === "solid" ? "" : v })}>
                <SelectTrigger data-testid={`combo-dash-${idx}`} className="bg-[color:var(--surface-2)] w-[76px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="solid">Solid</SelectItem>
                  <SelectItem value="dashed">Dashed</SelectItem>
                  <SelectItem value="dotted">Dotted</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid={`combo-opacity-${idx}`} type="number" min={0} max={1} step={0.05} value={c.opacity ?? ""} placeholder="op"
                onChange={(e) => upd(k, { opacity: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] h-6 text-[10px] w-[50px]" />
              {["area", "spline_area"].includes(c.type) && (
                <Input data-testid={`combo-area-opacity-${idx}`} type="number" min={0} max={1} step={0.05} value={c.areaOpacity ?? ""} placeholder="fill"
                  onChange={(e) => upd(k, { areaOpacity: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] h-6 text-[10px] w-[50px]" />
              )}
            </div>
          </div>
        );
      })}
      <p className="text-[10px] text-[color:var(--ink-3)]">Each measure renders independently — mix any number of bars and lines across two Y axes.</p>
    </Card>
  );
}

// ---------- Item 10: tornado styling ----------
export function TornadoStyleEditor({ opts, patchOpts }) {
  return (
    <Card icon={BarChart3} title="Tornado style" testid="tornado-style-editor">
      <Row label={`Bar thickness (${opts.tornadoBarWidth || 26}px)`}>
        <div className="w-[110px]"><Slider data-testid="tornado-bar-width" min={8} max={48} step={2} value={[opts.tornadoBarWidth || 26]} onValueChange={([v]) => patchOpts({ tornadoBarWidth: v })} /></div>
      </Row>
      <Row label="Label content">
        <Select value={opts.labelMode || "value"} onValueChange={(v) => patchOpts({ labelMode: v })}>
          <SelectTrigger data-testid="tornado-label-mode" className="bg-[color:var(--surface)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="value">Value</SelectItem>
            <SelectItem value="category">Category</SelectItem>
            <SelectItem value="series">Series name</SelectItem>
            <SelectItem value="percent">Percentage</SelectItem>
            <SelectItem value="value_percent">Value (percent)</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Label position">
        <Select value={opts.labelPosition || "auto"} onValueChange={(v) => patchOpts({ labelPosition: v === "auto" ? "" : v })}>
          <SelectTrigger data-testid="tornado-label-pos" className="bg-[color:var(--surface)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Outside (auto)</SelectItem>
            <SelectItem value="inside">Inside</SelectItem>
            <SelectItem value="insideLeft">Inside start</SelectItem>
            <SelectItem value="insideRight">Inside end</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Center axis color"><ColorField testid="tornado-axis-color" value={opts.tornadoAxisColor} fallback="#9AA3B8" onChange={(v) => patchOpts({ tornadoAxisColor: v })} /></Row>
    </Card>
  );
}

// ---------- Items 119/120/121: funnel styling ----------
export function FunnelStyleEditor({ opts, patchOpts }) {
  return (
    <Card icon={FunnelIcon} title="Funnel style" testid="funnelstyle-editor">
      <Row label="Label content">
        <Select value={opts.funnelLabelMode || "name"} onValueChange={(v) => patchOpts({ funnelLabelMode: v })}>
          <SelectTrigger data-testid="funnel-label-mode" className="bg-[color:var(--surface)] w-[160px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{FUNNEL_LABEL_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <Row label="Label position">
        <Select value={opts.funnelLabelPos || "inside"} onValueChange={(v) => patchOpts({ funnelLabelPos: v })}>
          <SelectTrigger data-testid="funnel-label-pos" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="inside">Inside</SelectItem>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Stage order">
        <Select value={opts.funnelSort || "descending"} onValueChange={(v) => patchOpts({ funnelSort: v })}>
          <SelectTrigger data-testid="funnel-sort" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="descending">Largest first</SelectItem>
            <SelectItem value="ascending">Smallest first</SelectItem>
            <SelectItem value="none">As queried</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Gradient stages"><Switch data-testid="funnel-gradient" checked={!!opts.funnelGradient} onCheckedChange={(v) => patchOpts({ funnelGradient: v })} /></Row>
      <SliderRow label={`Stage gap (${opts.funnelGap ?? 2}px)`} min={0} max={12} step={1} value={Number(opts.funnelGap ?? 2)} onChange={(v) => patchOpts({ funnelGap: v })} testid="funnel-gap" />
      <FunnelStageMapper opts={opts} patchOpts={patchOpts} />
    </Card>
  );
}

// ---------- Item 43: dynamic category → funnel stage mapping ----------
function FunnelStageMapper({ opts, patchOpts }) {
  const stages = opts.funnelStages || [];
  const setStages = (arr) => patchOpts({ funnelStages: arr });
  return (
    <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5" data-testid="funnel-stage-mapper">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Stage mapping</Label>
        <Button data-testid="funnel-add-stage" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]"
          onClick={() => setStages([...stages, { label: `Stage ${stages.length + 1}`, values: "", color: "" }])}>
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
      {!stages.length && <p className="text-[10px] text-[color:var(--ink-3)]">Rename, merge, order or exclude raw categories into named funnel stages.</p>}
      {stages.map((s, i) => (
        <div key={i} className="space-y-1 rounded-md border border-[color:var(--glass-border)] p-1.5">
          <div className="flex gap-1.5 items-center">
            <Input data-testid={`funnel-stage-label-${i}`} value={s.label || ""} placeholder="Stage name"
              onChange={(e) => setStages(stages.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              className="bg-[color:var(--surface-2)] flex-1 h-6 text-[10px]" />
            <input data-testid={`funnel-stage-color-${i}`} type="color" value={s.color || "#2B4EFF"}
              onChange={(e) => setStages(stages.map((x, j) => (j === i ? { ...x, color: e.target.value } : x)))}
              className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />
            <span className="flex flex-col shrink-0">
              <button title="Move up" disabled={i === 0} onClick={() => { const a = [...stages]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; setStages(a); }} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30 leading-none">▲</button>
              <button title="Move down" disabled={i === stages.length - 1} onClick={() => { const a = [...stages]; [a[i + 1], a[i]] = [a[i], a[i + 1]]; setStages(a); }} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30 leading-none">▼</button>
            </span>
            <button onClick={() => setStages(stages.filter((_, j) => j !== i))} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
          <Input data-testid={`funnel-stage-values-${i}`} value={Array.isArray(s.values) ? s.values.join(", ") : (s.values || "")} placeholder="Raw values, e.g. lead, prospect"
            onChange={(e) => setStages(stages.map((x, j) => (j === i ? { ...x, values: e.target.value } : x)))}
            className="bg-[color:var(--surface-2)] h-6 text-[10px]" />
        </div>
      ))}
      {stages.length > 0 && (
        <Row label="Unmapped values">
          <Select value={opts.funnelUnmapped || "keep"} onValueChange={(v) => patchOpts({ funnelUnmapped: v })}>
            <SelectTrigger data-testid="funnel-unmapped" className="bg-[color:var(--surface-2)] w-[120px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="keep">Keep as-is</SelectItem>
              <SelectItem value="other">Group as “Other”</SelectItem>
              <SelectItem value="exclude">Exclude</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      )}
    </div>
  );
}

// ---------- Items 122/124: scatter markers, borders, labels ----------
export function ScatterStyleEditor({ opts, patchOpts }) {
  return (
    <Card icon={ScatterChart} title="Marker style" testid="scatterstyle-editor">
      <Row label="Shape">
        <Select value={opts.markerShape || "circle"} onValueChange={(v) => patchOpts({ markerShape: v })}>
          <SelectTrigger data-testid="scatter-marker-shape" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{MARKER_SHAPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <SliderRow label={`Size (${opts.markerSize || 14}px)`} min={4} max={40} step={1} value={Number(opts.markerSize) || 14} onChange={(v) => patchOpts({ markerSize: v })} testid="scatter-marker-size" />
      <SliderRow label={`Opacity (${Math.round((opts.markerOpacity ?? 0.82) * 100)}%)`} min={10} max={100} step={5} value={Math.round((opts.markerOpacity ?? 0.82) * 100)} onChange={(v) => patchOpts({ markerOpacity: v / 100 })} testid="scatter-marker-opacity" />
      <Row label="Fill color"><ColorField testid="scatter-marker-color" value={opts.markerColor} fallback="#2B4EFF" onChange={(v) => patchOpts({ markerColor: v })} /></Row>
      <Row label="Border color"><ColorField testid="scatter-border-color" value={opts.markerBorderColor} fallback="#0A1029" onChange={(v) => patchOpts({ markerBorderColor: v })} /></Row>
      {opts.markerBorderColor && (
        <SliderRow label={`Border width (${opts.markerBorderWidth || 1}px)`} min={1} max={6} step={0.5} value={Number(opts.markerBorderWidth) || 1} onChange={(v) => patchOpts({ markerBorderWidth: v })} testid="scatter-border-width" />
      )}
      <Row label="Label content">
        <Select value={opts.scatterLabelMode || "name"} onValueChange={(v) => patchOpts({ scatterLabelMode: v })}>
          <SelectTrigger data-testid="scatter-label-mode" className="bg-[color:var(--surface)] w-[140px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{SCATTER_LABEL_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5" data-testid="bubble-editor">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Bubbles (3rd measure = size)</Label>
        <div className="grid grid-cols-2 gap-1.5">
          <NumInput testid="bubble-min" value={opts.bubbleMin} placeholder="Min size (8)" onChange={(v) => patchOpts({ bubbleMin: v })} />
          <NumInput testid="bubble-max" value={opts.bubbleMax} placeholder="Max size (40)" onChange={(v) => patchOpts({ bubbleMax: v })} />
        </div>
        <Row label="Color by"><Sel testid="bubble-color-mode" value={opts.bubbleColorMode || "auto"} onChange={(v) => patchOpts({ bubbleColorMode: v === "auto" ? "" : v })} items={[["auto", "Auto"], ["category", "Each point"], ["single", "Single color"], ["series", "2nd dimension"], ["value", "Value gradient"]]} /></Row>
        {opts.bubbleColorMode === "value" && (
          <>
            <Row label="Gradient measure"><Sel testid="bubble-color-dim" value={opts.bubbleColorDim || "size"} onChange={(v) => patchOpts({ bubbleColorDim: v })} items={[["size", "Size measure"], ["y", "Y measure"], ["x", "X measure"]]} /></Row>
            <Row label="Gradient legend"><Sel testid="bubble-legend-pos" value={opts.bubbleLegendPos || "right"} onChange={(v) => patchOpts({ bubbleLegendPos: v })} items={[["right", "Right"], ["bottom", "Bottom"], ["hidden", "Hidden"]]} /></Row>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-[11px] text-[color:var(--ink-3)]">Low / mid / high</Label>
              <div className="flex items-center gap-1">
                <ColorField testid="bubble-low" value={opts.heatLow} fallback="#E8EDFF" onChange={(v) => patchOpts({ heatLow: v })} />
                <ColorField testid="bubble-mid" value={opts.heatMid} fallback="#2B4EFF" onChange={(v) => patchOpts({ heatMid: v })} />
                <ColorField testid="bubble-high" value={opts.heatHigh} fallback="#101E66" onChange={(v) => patchOpts({ heatHigh: v })} />
              </div>
            </div>
          </>
        )}
        <p className="text-[10px] text-[color:var(--ink-3)]">Add a 2nd dimension in Data to group bubbles into coloured series with a legend.</p>
      </div>
    </Card>
  );
}

// ---------- Items 133/134: radar draw type + markers ----------
export function RadarStyleEditor({ opts, patchOpts, config }) {
  const showMarkers = typeof opts.showMarkers === "boolean" ? opts.showMarkers : true;
  return (
    <Card icon={RadarIcon} title="Radar style" testid="radarstyle-editor">
      <Row label="Grid shape">
        <Select value={opts.radarShape || "polygon"} onValueChange={(v) => patchOpts({ radarShape: v })}>
          <SelectTrigger data-testid="radar-shape" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="polygon">Polygon</SelectItem>
            <SelectItem value="circle">Circle</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Draw type">
        <Select value={opts.radarDraw || "area"} onValueChange={(v) => patchOpts({ radarDraw: v })}>
          <SelectTrigger data-testid="radar-draw" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="area">Filled area</SelectItem>
            <SelectItem value="line">Line only</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      {(opts.radarDraw || "area") === "area" && (
        <SliderRow label={`Fill opacity (${Math.round((opts.radarOpacity ?? 0.22) * 100)}%)`} min={5} max={80} step={5} value={Math.round((opts.radarOpacity ?? 0.22) * 100)} onChange={(v) => patchOpts({ radarOpacity: v / 100 })} testid="radar-opacity" />
      )}
      <Row label="Show markers"><Switch data-testid="radar-markers" checked={showMarkers} onCheckedChange={(v) => patchOpts({ showMarkers: v })} /></Row>
      {showMarkers && (
        <>
          <Row label="Marker shape">
            <Select value={opts.markerShape || "circle"} onValueChange={(v) => patchOpts({ markerShape: v })}>
              <SelectTrigger data-testid="radar-marker-shape" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{MARKER_SHAPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          <SliderRow label={`Marker size (${opts.markerSize || 6}px)`} min={2} max={16} step={1} value={Number(opts.markerSize) || 6} onChange={(v) => patchOpts({ markerSize: v })} testid="radar-marker-size" />
        </>
      )}
      <SliderRow label={`Line width (${opts.lineWidth || 2.5}px)`} min={1} max={6} step={0.5} value={Number(opts.lineWidth) || 2.5} onChange={(v) => patchOpts({ lineWidth: v })} testid="radar-line-width" />
      <Row label="Axis max">
        <NumInput testid="radar-max" value={opts.radarMax} placeholder="Auto" onChange={(v) => patchOpts({ radarMax: v })} className="w-[90px]" />
      </Row>
      <Row label="Scale"><Sel testid="radar-max-mode" value={opts.radarMaxMode || "shared"} onChange={(v) => patchOpts({ radarMaxMode: v })} items={[["shared", "Shared max"], ["per_axis", "Per-axis max"], ["per_series", "Per-measure (% of own max)"]]} /></Row>
      {opts.radarMaxMode === "per_series" && <p className="text-[10px] text-[color:var(--ink-3)]">Each measure is drawn as a share of its own maximum, so measures with different magnitudes stay comparable. Labels and tooltips keep the real values.</p>}
      <Row label="Axis labels"><Switch data-testid="radar-axis-labels" checked={opts.radarAxisLabels !== false} onCheckedChange={(v) => patchOpts({ radarAxisLabels: v })} /></Row>
      {opts.radarAxisLabels !== false && (
        <>
          <Row label="Axis label style">
            <span className="flex items-center gap-1.5">
              <ColorField testid="radar-axis-color" value={opts.radarAxisColor} fallback="#232B4A" onChange={(v) => patchOpts({ radarAxisColor: v })} />
              <NumInput testid="radar-axis-size" value={opts.radarAxisSize} placeholder="11" onChange={(v) => patchOpts({ radarAxisSize: v })} className="w-12" />
              <Switch data-testid="radar-axis-bold" checked={!!opts.radarAxisBold} onCheckedChange={(v) => patchOpts({ radarAxisBold: v })} />
            </span>
          </Row>
          <Row label="Wrap labels after (chars)"><NumInput testid="radar-axis-maxlen" value={opts.radarAxisMaxLen} placeholder="18" onChange={(v) => patchOpts({ radarAxisMaxLen: v })} className="w-[70px]" /></Row>
        </>
      )}
      <Row label="Scale value labels"><Switch data-testid="radar-scale-labels" checked={!!opts.radarScaleLabels} onCheckedChange={(v) => patchOpts({ radarScaleLabels: v })} /></Row>
      <Row label="Rings"><NumInput testid="radar-splits" value={opts.radarSplits} placeholder="5" onChange={(v) => patchOpts({ radarSplits: v })} className="w-[70px]" /></Row>
      <Row label="Ring lines"><Switch data-testid="radar-split-lines" checked={opts.radarSplitLines !== false} onCheckedChange={(v) => patchOpts({ radarSplitLines: v })} /></Row>
      <Row label="Ring shading"><Switch data-testid="radar-split-area" checked={opts.radarSplitArea !== false} onCheckedChange={(v) => patchOpts({ radarSplitArea: v })} /></Row>
      <Row label="Radius (%)"><NumInput testid="radar-radius" value={opts.radarRadius} placeholder="Auto" onChange={(v) => patchOpts({ radarRadius: v })} className="w-[70px]" /></Row>
      <Row label="Value labels on points"><Switch data-testid="radar-value-labels" checked={!!opts.showLabels} onCheckedChange={(v) => patchOpts({ showLabels: v })} /></Row>
      {opts.showLabels && <Row label="Value label size"><NumInput testid="radar-label-size" value={opts.radarLabelSize} placeholder="10" onChange={(v) => patchOpts({ radarLabelSize: v })} className="w-[70px]" /></Row>}
      <Row label="Hover focus (dim others)"><Switch data-testid="radar-focus" checked={opts.radarFocus !== false} onCheckedChange={(v) => patchOpts({ radarFocus: v })} /></Row>
      <Row label="Click to select series"><Switch data-testid="radar-select" checked={!!opts.radarSelect} onCheckedChange={(v) => patchOpts({ radarSelect: v })} /></Row>
      <p className="text-[10px] text-[color:var(--ink-3)]">Clicking an axis label cross-filters other widgets by that category. Legend toggles series.</p>
      {(config?.measures || []).length > 0 && <RadarSeriesEditor config={config} opts={opts} patchOpts={patchOpts} />}
      <p className="text-[10px] text-[color:var(--ink-3)]">Add more measures in Data to overlay multiple radar series.</p>
    </Card>
  );
}

// Item 51: independent per-series (measure) formatting for radar
function RadarSeriesEditor({ config, opts, patchOpts }) {
  const rs = opts.radarSeries || {};
  const keys = (config.measures || []).map((m) => m.key || m.alias || `${m.agg || "sum"}_${m.field}`);
  const labelFor = (m, k) => m.label || (opts.seriesLabels || {})[k] || k;
  const set = (k, patch) => patchOpts({ radarSeries: { ...rs, [k]: { ...(rs[k] || {}), ...patch } } });
  return (
    <div data-testid="radar-series-editor" className="space-y-1.5 rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2">
      <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Per-series formatting</Label>
      {(config.measures || []).map((m, i) => {
        const k = keys[i]; const c = rs[k] || {};
        return (
          <div key={k} className="space-y-1">
            <div className="text-[10px] text-[color:var(--ink-3)] truncate">{labelFor(m, k)}</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <input data-testid={`radar-series-color-${i}`} type="color" title="Color" value={c.color || "#2B4EFF"} onChange={(e) => set(k, { color: e.target.value })} className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
              <NumInput testid={`radar-series-width-${i}`} value={c.width} placeholder="Width" onChange={(v) => set(k, { width: v })} className="w-14" />
              <Sel testid={`radar-series-dash-${i}`} value={c.dash || "solid"} onChange={(v) => set(k, { dash: v })} items={[["solid", "Solid"], ["dashed", "Dashed"], ["dotted", "Dotted"]]} width="w-[84px]" />
              <Sel testid={`radar-series-draw-${i}`} value={c.draw || "inherit"} onChange={(v) => set(k, { draw: v === "inherit" ? "" : v })} items={[["inherit", "Draw: default"], ["area", "Filled"], ["line", "Line only"]]} width="w-[110px]" />
              <NumInput testid={`radar-series-opacity-${i}`} value={c.opacity} placeholder="Fill α" onChange={(v) => set(k, { opacity: v })} className="w-14" />
              <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]"><Switch data-testid={`radar-series-labels-${i}`} checked={!!c.labels} onCheckedChange={(v) => set(k, { labels: v })} />values</label>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Items 135/136: gauge target, bands, needle, label toggles ----------
export function GaugeStyleEditor({ opts, patchOpts }) {
  const bands = opts.gaugeBands || [];
  const setBands = (arr) => patchOpts({ gaugeBands: arr });
  return (
    <Card icon={GaugeIcon} title="Gauge" testid="gaugestyle-editor">
      <div className="grid grid-cols-2 gap-1.5">
        <NumInput testid="gauge-min" value={opts.gaugeMin} placeholder="Min (0)" onChange={(v) => patchOpts({ gaugeMin: v })} />
        <NumInput testid="gauge-max" value={opts.max} placeholder="Max (auto)" onChange={(v) => patchOpts({ max: v })} />
      </div>
      <Row label="Gauge type">
        <Select value={opts.gaugeKind || "three_quarter"} onValueChange={(v) => patchOpts({ gaugeKind: v })}>
          <SelectTrigger data-testid="gauge-kind" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="three_quarter">Three-quarter</SelectItem>
            <SelectItem value="semi">Semi-circle</SelectItem>
            <SelectItem value="full">Full circle</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Target value">
        <NumInput testid="gauge-target" value={opts.gaugeTarget} placeholder="None" onChange={(v) => patchOpts({ gaugeTarget: v })} className="w-[110px]" />
      </Row>
      <p className="text-[10px] text-[color:var(--ink-3)]">Tip: a 2nd measure is used as the target automatically when set.</p>
      <SliderRow label={`Arc width (${opts.gaugeWidth || 16}px)`} min={6} max={40} step={2} value={Number(opts.gaugeWidth) || 16} onChange={(v) => patchOpts({ gaugeWidth: v })} testid="gauge-width" />
      <Row label="Needle"><Switch data-testid="gauge-needle" checked={opts.showNeedle ?? (bands.length > 0)} onCheckedChange={(v) => patchOpts({ showNeedle: v })} /></Row>
      {(opts.showNeedle ?? bands.length > 0) && (
        <Row label="Pointer">
          <span className="flex items-center gap-1.5">
            <input data-testid="gauge-pointer-color" type="color" title="Pointer color" value={opts.pointerColor || "#1A2440"} onChange={(e) => patchOpts({ pointerColor: e.target.value })} className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
            <NumInput testid="gauge-pointer-width" value={opts.pointerWidth} placeholder="5" onChange={(v) => patchOpts({ pointerWidth: v })} className="w-12" />
          </span>
        </Row>
      )}
      <Row label="Show current value"><Switch data-testid="gauge-show-current" checked={opts.showCurrentLabel !== false} onCheckedChange={(v) => patchOpts({ showCurrentLabel: v })} /></Row>
      {opts.showCurrentLabel !== false && (
        <>
          <Row label="Value style">
            <span className="flex items-center gap-1.5">
              <input data-testid="gauge-value-color" type="color" title="Value color" value={opts.gaugeValueColor || "#1A2440"} onChange={(e) => patchOpts({ gaugeValueColor: e.target.value })} className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
              <NumInput testid="gauge-value-size" value={opts.gaugeValueSize} placeholder="22" onChange={(v) => patchOpts({ gaugeValueSize: v })} className="w-12" />
            </span>
          </Row>
          <FmtRow label="Value format" testid="gauge-value-fmt" value={opts.gaugeValueFormat} onChange={(v) => patchOpts({ gaugeValueFormat: v })} />
          <Row label="Value caption"><Input data-testid="gauge-value-title" value={opts.gaugeValueTitle || ""} placeholder="e.g. Revenue" onChange={(e) => patchOpts({ gaugeValueTitle: e.target.value })} className="bg-[color:var(--surface)] w-[120px] h-6 text-[10px]" /></Row>
        </>
      )}
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5" data-testid="gauge-scale-editor">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Scale</Label>
        <Row label="Tick marks"><Switch data-testid="gauge-show-scale" checked={!!opts.gaugeShowScale} onCheckedChange={(v) => patchOpts({ gaugeShowScale: v })} /></Row>
        <Row label="Scale labels"><Switch data-testid="gauge-scale-labels" checked={opts.gaugeScaleLabels !== false} onCheckedChange={(v) => patchOpts({ gaugeScaleLabels: v })} /></Row>
        <Row label="Divisions"><NumInput testid="gauge-splits" value={opts.gaugeSplits} placeholder="5" onChange={(v) => patchOpts({ gaugeSplits: v })} className="w-[70px]" /></Row>
        <Row label="Scale style">
          <span className="flex items-center gap-1.5">
            <ColorField testid="gauge-scale-color" value={opts.gaugeScaleColor} fallback="#94A3B8" onChange={(v) => patchOpts({ gaugeScaleColor: v })} />
            <NumInput testid="gauge-scale-size" value={opts.gaugeScaleSize} placeholder="10" onChange={(v) => patchOpts({ gaugeScaleSize: v })} className="w-12" />
          </span>
        </Row>
        <FmtRow label="Scale format" testid="gauge-scale-fmt" value={opts.gaugeScaleFormat} onChange={(v) => patchOpts({ gaugeScaleFormat: v })} hint="Default: measure format, compact (K/M) above 10,000" />
      </div>
      <Row label="Show target label"><Switch data-testid="gauge-show-target" checked={opts.showTargetLabel !== false} onCheckedChange={(v) => patchOpts({ showTargetLabel: v })} /></Row>
      {opts.showTargetLabel !== false && (
        <>
          <Row label="Target text">
            <Input data-testid="gauge-target-text" value={opts.gaugeTargetText || ""} placeholder="Target" onChange={(e) => patchOpts({ gaugeTargetText: e.target.value })} className="bg-[color:var(--surface)] w-[120px] h-6 text-[10px]" />
          </Row>
          <Row label="Target shows"><Sel testid="gauge-target-mode" value={opts.gaugeTargetMode || "label"} onChange={(v) => patchOpts({ gaugeTargetMode: v })} items={[["label", "Label + value"], ["value_only", "Value only"], ["delta", "+ difference"], ["pct", "+ % of target"]]} /></Row>
          <Row label="Target style">
            <span className="flex items-center gap-1.5">
              <input data-testid="gauge-target-color" type="color" title="Target marker color" value={opts.gaugeTargetColor || "#FF6B35"} onChange={(e) => patchOpts({ gaugeTargetColor: e.target.value })} className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
              <ColorField testid="gauge-target-label-color" value={opts.gaugeTargetLabelColor} fallback="#5A6284" onChange={(v) => patchOpts({ gaugeTargetLabelColor: v })} />
              <NumInput testid="gauge-target-size" value={opts.gaugeTargetSize} placeholder="11" onChange={(v) => patchOpts({ gaugeTargetSize: v })} className="w-12" />
            </span>
          </Row>
          <FmtRow label="Target format" testid="gauge-target-fmt" value={opts.gaugeTargetFormat} onChange={(v) => patchOpts({ gaugeTargetFormat: v })} />
        </>
      )}
      <div className="flex items-center justify-between pt-1">
        <Label className="text-[11px] text-[color:var(--ink-3)]">Threshold bands (up to value → color)</Label>
        <Button data-testid="gauge-add-band" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={() => setBands([...bands, { to: "", color: "#E24B5B" }])}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
      {bands.map((b, i) => (
        <div key={i} className="flex gap-1.5 items-center">
          <NumInput testid={`gauge-band-to-${i}`} value={b.to} placeholder="Up to…" onChange={(v) => setBands(bands.map((x, j) => j === i ? { ...x, to: v } : x))} className="flex-1" />
          <input data-testid={`gauge-band-color-${i}`} type="color" value={b.color || "#E24B5B"} onChange={(e) => setBands(bands.map((x, j) => j === i ? { ...x, color: e.target.value } : x))} className="h-7 w-9 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />
          <button onClick={() => setBands(bands.filter((_, j) => j !== i))} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] p-0.5 shrink-0"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </Card>
  );
}

// ---------- Items 129/130: treemap hierarchy formatting ----------
export function TreemapStyleEditor({ opts, patchOpts }) {
  return (
    <Card icon={LayoutGrid} title="Treemap tiles" testid="treemapstyle-editor">
      <SliderRow label={`Tile padding (${opts.treemapPadding ?? 2}px)`} min={0} max={12} step={1} value={Number(opts.treemapPadding ?? 2)} onChange={(v) => patchOpts({ treemapPadding: v })} testid="treemap-padding" />
      <Row label="Label position">
        <Select value={opts.treemapLabelPos || "inside"} onValueChange={(v) => patchOpts({ treemapLabelPos: v })}>
          <SelectTrigger data-testid="treemap-label-pos" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="inside">Center</SelectItem>
            <SelectItem value="insideTopLeft">Top left</SelectItem>
            <SelectItem value="insideBottomLeft">Bottom left</SelectItem>
            <SelectItem value="insideTopRight">Top right</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Group headers"><Switch data-testid="treemap-upper-label" checked={opts.treemapUpperLabel !== false} onCheckedChange={(v) => patchOpts({ treemapUpperLabel: v })} /></Row>
      {opts.treemapUpperLabel !== false && (
        <>
          <Row label="Header shows total"><Switch data-testid="treemap-upper-values" checked={!!opts.treemapUpperValues} onCheckedChange={(v) => patchOpts({ treemapUpperValues: v })} /></Row>
          <Row label="Header height"><NumInput testid="treemap-upper-size" value={opts.treemapUpperLabelSize} placeholder="22" onChange={(v) => patchOpts({ treemapUpperLabelSize: v })} className="w-[70px]" /></Row>
          <Row label="Header text color"><ColorField testid="treemap-upper-color" value={opts.treemapUpperLabelColor} fallback="#ffffff" onChange={(v) => patchOpts({ treemapUpperLabelColor: v })} /></Row>
        </>
      )}
      <Row label="Breadcrumb trail"><Switch data-testid="treemap-breadcrumb" checked={!!opts.treemapBreadcrumb} onCheckedChange={(v) => patchOpts({ treemapBreadcrumb: v })} /></Row>
      <SliderRow label={`Border width (${opts.treemapBorderWidth ?? 2}px)`} min={0} max={6} step={1} value={Number(opts.treemapBorderWidth ?? 2)} onChange={(v) => patchOpts({ treemapBorderWidth: v })} testid="treemap-border-width" />
      <Row label="Border color"><ColorField testid="treemap-border-color" value={opts.treemapBorderColor} fallback="#ffffff" onChange={(v) => patchOpts({ treemapBorderColor: v })} /></Row>
      <Row label="Sorting">
        <Select value={opts.treemapSort || "desc"} onValueChange={(v) => patchOpts({ treemapSort: v })}>
          <SelectTrigger data-testid="treemap-sort" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Largest first</SelectItem>
            <SelectItem value="asc">Smallest first</SelectItem>
            <SelectItem value="none">As queried</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Show values in tiles"><Switch data-testid="treemap-show-values" checked={!!opts.treemapShowValues || (!!opts.treemapValueMode && opts.treemapValueMode !== "name")} onCheckedChange={(v) => patchOpts({ treemapShowValues: v, treemapValueMode: v ? (opts.treemapValueMode && opts.treemapValueMode !== "name" ? opts.treemapValueMode : "value") : "name" })} /></Row>
      {(opts.treemapShowValues || (opts.treemapValueMode && opts.treemapValueMode !== "name")) && (
        <Row label="Tile text"><Sel testid="treemap-value-mode" value={opts.treemapValueMode || "value"} onChange={(v) => patchOpts({ treemapValueMode: v })} items={[["value", "Name + value"], ["percent", "Name + % share"], ["value_percent", "Name + value + %"], ["value_only", "Value only"]]} /></Row>
      )}
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5" data-testid="treemap-label-scale-editor">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Label sizing by tile size</Label>
        <Row label="Font size"><Sel testid="treemap-label-scale" value={opts.treemapLabelScale || "fixed"} onChange={(v) => patchOpts({ treemapLabelScale: v })} items={[["fixed", "Fixed"], ["auto", "Scale with tile"]]} /></Row>
        {(opts.treemapLabelScale || "fixed") === "fixed"
          ? <Row label="Size (px)"><NumInput testid="treemap-label-fontsize" value={opts.treemapLabelFontSize} placeholder="12" onChange={(v) => patchOpts({ treemapLabelFontSize: v })} className="w-[70px]" /></Row>
          : (
            <div className="grid grid-cols-2 gap-1.5">
              <NumInput testid="treemap-label-min" value={opts.treemapLabelMin} placeholder="Min px (9)" onChange={(v) => patchOpts({ treemapLabelMin: v })} />
              <NumInput testid="treemap-label-max" value={opts.treemapLabelMax} placeholder="Max px (18)" onChange={(v) => patchOpts({ treemapLabelMax: v })} />
            </div>
          )}
        <Row label="Hide labels under (% share)"><NumInput testid="treemap-hide-small" value={opts.treemapHideSmallPct} placeholder="0 = never" onChange={(v) => patchOpts({ treemapHideSmallPct: v })} className="w-[90px]" /></Row>
        <Row label="Label color">
          <span className="flex items-center gap-1.5">
            <Button data-testid="treemap-label-auto" size="sm" variant={(opts.treemapLabelColor || "auto") === "auto" ? "default" : "outline"} className="h-6 text-[10px] px-2" onClick={() => patchOpts({ treemapLabelColor: "auto" })}>Auto contrast</Button>
            <ColorField testid="treemap-label-color" value={(opts.treemapLabelColor || "auto") === "auto" ? "" : opts.treemapLabelColor} fallback="#ffffff" onChange={(v) => patchOpts({ treemapLabelColor: v })} />
          </span>
        </Row>
        <Row label="Long labels"><Sel testid="treemap-label-overflow" value={opts.treemapLabelOverflow || "truncate"} onChange={(v) => patchOpts({ treemapLabelOverflow: v })} items={[["truncate", "Truncate…"], ["break", "Wrap"], ["none", "Overflow"]]} /></Row>
      </div>
      <p className="text-[10px] text-[color:var(--ink-3)]">Add a 2nd dimension for a parent → child hierarchy with group headers.</p>
    </Card>
  );
}

// ---------- Items 131 + 49/50: heatmap scale, legend, axes, labels, cells ----------
const HEAT_SORTS = [["auto", "As queried"], ["asc", "A → Z"], ["desc", "Z → A"], ["value_desc", "Total ↓"], ["value_asc", "Total ↑"]];
function Sel({ value, onChange, items, testid, width = "w-[130px]" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`bg-[color:var(--surface)] ${width} h-7 text-xs`}><SelectValue /></SelectTrigger>
      <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}
export function HeatmapStyleEditor({ opts, patchOpts }) {
  const lblMode = opts.heatLabelMode || (opts.showLabels ? "value" : "none");
  return (
    <>
      <Card icon={Grid3x3} title="Heat scale & legend" testid="heatmapstyle-editor">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-[11px] text-[color:var(--ink-3)]">Min / mid / max colors</Label>
          <div className="flex items-center gap-1">
            <ColorField testid="heat-low" value={opts.heatLow} fallback="#E8EDFF" onChange={(v) => patchOpts({ heatLow: v })} />
            <ColorField testid="heat-mid" value={opts.heatMid} fallback="#2B4EFF" onChange={(v) => patchOpts({ heatMid: v })} />
            <ColorField testid="heat-high" value={opts.heatHigh} fallback="#101E66" onChange={(v) => patchOpts({ heatHigh: v })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <NumInput testid="heat-min" value={opts.heatMin} placeholder="Scale min (auto)" onChange={(v) => patchOpts({ heatMin: v })} />
          <NumInput testid="heat-max" value={opts.heatMax} placeholder="Scale max (auto)" onChange={(v) => patchOpts({ heatMax: v })} />
        </div>
        <Row label="Legend position"><Sel testid="heat-legend-pos" value={opts.heatLegendPos || "bottom"} onChange={(v) => patchOpts({ heatLegendPos: v })} items={[["bottom", "Bottom"], ["top", "Top"], ["right", "Right"], ["left", "Left"], ["hidden", "Hidden"]]} /></Row>
        <Row label="Legend type"><Sel testid="heat-legend-type" value={opts.heatLegendType || "continuous"} onChange={(v) => patchOpts({ heatLegendType: v })} items={[["continuous", "Gradient bar"], ["piecewise", "Stepped ranges"]]} /></Row>
        {opts.heatLegendType === "piecewise" && <Row label="Steps"><NumInput testid="heat-steps" value={opts.heatSteps} placeholder="5" onChange={(v) => patchOpts({ heatSteps: v })} className="w-[70px]" /></Row>}
        <Row label="Legend title"><Input data-testid="heat-legend-title" value={opts.heatLegendTitle || ""} placeholder="(measure)" onChange={(e) => patchOpts({ heatLegendTitle: e.target.value })} className="bg-[color:var(--surface)] w-[130px] h-7 text-xs" /></Row>
      </Card>
      <Card icon={Ruler} title="Heatmap axes" testid="heatmap-axes-editor">
        <Row label="Swap X / Y"><Switch data-testid="heat-swap-axes" checked={!!opts.heatSwapAxes} onCheckedChange={(v) => patchOpts({ heatSwapAxes: v })} /></Row>
        <Row label="Rows top → down"><Switch data-testid="heat-y-topdown" checked={!!opts.heatYTopDown} onCheckedChange={(v) => patchOpts({ heatYTopDown: v })} /></Row>
        <Row label="Sort X"><Sel testid="heat-sort-x" value={opts.heatSortX || "auto"} onChange={(v) => patchOpts({ heatSortX: v })} items={HEAT_SORTS} /></Row>
        <Row label="Sort Y"><Sel testid="heat-sort-y" value={opts.heatSortY || "auto"} onChange={(v) => patchOpts({ heatSortY: v })} items={HEAT_SORTS} /></Row>
        <Row label="Auto titles from fields"><Switch data-testid="heat-auto-titles" checked={!!opts.axisAutoTitles} onCheckedChange={(v) => patchOpts({ axisAutoTitles: v })} /></Row>
        <div className="grid grid-cols-2 gap-1.5">
          <Input data-testid="heat-x-title" value={opts.xTitle || ""} onChange={(e) => patchOpts({ xTitle: e.target.value })} placeholder="X-axis title" className="bg-[color:var(--surface)] h-7 text-xs" />
          <Input data-testid="heat-y-title" value={opts.yTitle || ""} onChange={(e) => patchOpts({ yTitle: e.target.value })} placeholder="Y-axis title" className="bg-[color:var(--surface)] h-7 text-xs" />
        </div>
        <Row label="X label rotation (°)"><NumInput testid="heat-label-rotation" value={opts.labelRotation} placeholder="Auto" onChange={(v) => patchOpts({ labelRotation: v })} className="w-[80px]" /></Row>
      </Card>
      <Card icon={Tags} title="Cell labels & formatting" testid="heatmap-cells-editor">
        <Row label="Label content"><Sel testid="heat-label-mode" value={lblMode} onChange={(v) => patchOpts({ heatLabelMode: v, showLabels: v !== "none" })} items={[["none", "None"], ["value", "Value"], ["percent_total", "% of total"], ["percent_row", "% of row"], ["percent_col", "% of column"], ["value_percent", "Value + % total"]]} /></Row>
        {lblMode !== "none" && (
          <>
            <Row label="Label size"><NumInput testid="heat-label-size" value={opts.heatLabelSize} placeholder="Auto" onChange={(v) => patchOpts({ heatLabelSize: v })} className="w-[70px]" /></Row>
            <Row label="Label color">
              <span className="flex items-center gap-1.5">
                <Button data-testid="heat-label-auto" size="sm" variant={(opts.heatLabelColor || "auto") === "auto" ? "default" : "outline"} className="h-6 text-[10px] px-2" onClick={() => patchOpts({ heatLabelColor: "auto" })}>Auto contrast</Button>
                <ColorField testid="heat-label-color" value={(opts.heatLabelColor || "auto") === "auto" ? "" : opts.heatLabelColor} fallback="#0A1029" onChange={(v) => patchOpts({ heatLabelColor: v })} />
              </span>
            </Row>
            <Row label="Bold labels"><Switch data-testid="heat-label-bold" checked={!!opts.heatLabelBold} onCheckedChange={(v) => patchOpts({ heatLabelBold: v })} /></Row>
            <Row label="% decimals"><NumInput testid="heat-pct-decimals" value={opts.heatPctDecimals} placeholder="1" onChange={(v) => patchOpts({ heatPctDecimals: v })} className="w-[70px]" /></Row>
          </>
        )}
        <SliderRow label={`Cell border (${opts.heatBorderWidth ?? 1}px)`} min={0} max={6} step={1} value={Number(opts.heatBorderWidth ?? 1)} onChange={(v) => patchOpts({ heatBorderWidth: v })} testid="heat-border-width" />
        <Row label="Border color"><ColorField testid="heat-border-color" value={opts.heatBorderColor} fallback="#ffffff" onChange={(v) => patchOpts({ heatBorderColor: v })} /></Row>
        <SliderRow label={`Cell radius (${opts.heatCellRadius ?? 4}px)`} min={0} max={16} step={1} value={Number(opts.heatCellRadius ?? 4)} onChange={(v) => patchOpts({ heatCellRadius: v })} testid="heat-cell-radius" />
        <Row label="Show empty cells"><Switch data-testid="heat-show-empty" checked={opts.heatShowEmpty !== false} onCheckedChange={(v) => patchOpts({ heatShowEmpty: v })} /></Row>
        {opts.heatShowEmpty !== false && (
          <>
            <Row label="Empty cell color"><ColorField testid="heat-null-color" value={opts.heatNullColor} fallback="#F1F3F9" onChange={(v) => patchOpts({ heatNullColor: v })} /></Row>
            <Row label="Empty cell text"><Input data-testid="heat-empty-text" value={opts.heatEmptyText || ""} placeholder="—" onChange={(e) => patchOpts({ heatEmptyText: e.target.value })} className="bg-[color:var(--surface)] w-[90px] h-7 text-xs" /></Row>
          </>
        )}
        <Row label="Tooltip shares (% row/col/total)"><Switch data-testid="heat-tooltip-shares" checked={opts.heatTooltipShares !== false} onCheckedChange={(v) => patchOpts({ heatTooltipShares: v })} /></Row>
      </Card>
    </>
  );
}

// ---------- Suggestion 1: per-series / per-category color overrides ----------
export function SeriesColorsEditor({ opts, patchOpts }) {
  const list = opts.seriesColors || [];
  const set = (arr) => patchOpts({ seriesColors: arr });
  return (
    <Card icon={Palette} title="Series / category colors" testid="seriescolors-editor">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] text-[color:var(--ink-3)]">Override colors by exact name</Label>
        <Button data-testid="seriescolors-add" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={() => set([...list, { name: "", color: "#2B4EFF" }])}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
      {list.map((s, i) => (
        <div key={i} className="flex gap-1.5 items-center">
          <Input data-testid={`seriescolor-name-${i}`} value={s.name} placeholder="Series / category name" onChange={(e) => set(list.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="bg-[color:var(--surface)] flex-1 min-w-0 h-7 text-xs" />
          <input data-testid={`seriescolor-color-${i}`} type="color" value={s.color || "#2B4EFF"} onChange={(e) => set(list.map((x, j) => j === i ? { ...x, color: e.target.value } : x))} className="h-7 w-9 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />
          <button onClick={() => set(list.filter((_, j) => j !== i))} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] p-0.5 shrink-0"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </Card>
  );
}

// ---------- Item 128: ECharts vector-map zoom controls ----------
export function MapStyleEditor({ opts, patchOpts, widget, config, patchConfig, numericFields }) {
  // Custom uploaded GeoJSON territories + built-in state maps (items 45-47 + custom import)
  const [customMaps, setCustomMaps] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [matchOpen, setMatchOpen] = React.useState(false);
  const fileRef = React.useRef(null);
  const refresh = React.useCallback(() => {
    api.get("/geo-maps").then(({ data }) => setCustomMaps(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);
  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("GeoJSON too large (max 20MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/geo-maps", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success(`Map "${data.name}" uploaded (${data.feature_count} regions)`);
      refresh();
      patchOpts({ mapShape: `custom:${data.id}` });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const removeMap = async (m) => {
    try {
      await api.delete(`/geo-maps/${m.id}`);
      if (opts.mapShape === `custom:${m.id}`) patchOpts({ mapShape: "" });
      refresh();
      toast.success("Custom map deleted");
    } catch {
      toast.error("Delete failed");
    }
  };
  return (
    <Card icon={MapIcon} title="Map controls" testid="mapstyle-editor">
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Map area (shape kind)</Label>
        <Select value={opts.mapShape || "world"} onValueChange={(v) => patchOpts({ mapShape: v === "world" ? "" : v })}>
          <SelectTrigger data-testid="map-shape-select" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="world">World — countries</SelectItem>
            {BUILTIN_STATE_MAPS.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
            {customMaps.map((m) => <SelectItem key={m.id} value={`custom:${m.id}`}>{m.name} — custom ({m.feature_count})</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-[color:var(--ink-3)] mt-1">State/province maps expect the location dimension to contain state names (e.g. “Maharashtra”, “Lagos”, “Dubai”).</p>
        <Button data-testid="territory-match-btn" size="sm" variant="outline" className="h-6 text-[10px] px-2 mt-1.5 w-full"
          disabled={!widget?.dataset_id} onClick={() => setMatchOpen(true)}>
          <MapPin className="w-3 h-3 mr-1" />
          Match territories{Object.keys(opts.territoryMap || {}).length ? ` (${Object.keys(opts.territoryMap).length} remapped)` : ""}
        </Button>
        {matchOpen && <TerritoryMatchDialog open={matchOpen} onOpenChange={setMatchOpen} widget={widget} config={config} opts={opts} patchOpts={patchOpts} />}
      </div>
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Custom territories</Label>
          <Button data-testid="geomap-upload-btn" size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={uploading}
            onClick={() => fileRef.current?.click()}>
            <Upload className="w-3 h-3 mr-1" />{uploading ? "Uploading…" : "Upload GeoJSON"}
          </Button>
          <input ref={fileRef} data-testid="geomap-upload-input" type="file" accept=".json,.geojson,application/json" className="hidden" onChange={onUpload} />
        </div>
        {!customMaps.length && <p className="text-[10px] text-[color:var(--ink-3)]">Upload a GeoJSON FeatureCollection of your sales territories — each region needs a name property. Max 20MB.</p>}
        {customMaps.map((m) => (
          <div key={m.id} data-testid={`geomap-item-${m.id}`} className="flex items-center gap-1.5 text-[11px] text-[color:var(--ink-2)]">
            <span className="truncate flex-1">{m.name} <span className="text-[color:var(--ink-3)]">({m.feature_count} regions)</span></span>
            <button data-testid={`geomap-delete-${m.id}`} title="Delete custom map" onClick={() => removeMap(m)}
              className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <SliderRow label={`Initial zoom (${opts.mapZoom || 1}×)`} min={1} max={8} step={0.5} value={Number(opts.mapZoom) || 1} onChange={(v) => patchOpts({ mapZoom: v })} testid="map-zoom" />
      <Row label="Zoom buttons"><Switch data-testid="map-zoom-controls" checked={opts.mapZoomControls !== false} onCheckedChange={(v) => patchOpts({ mapZoomControls: v })} /></Row>
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5" data-testid="map-format-editor">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Colors, legend & labels</Label>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-[11px] text-[color:var(--ink-3)]">Low / mid / high</Label>
          <div className="flex items-center gap-1">
            <ColorField testid="map-low" value={opts.mapLow} fallback="#E8EDFF" onChange={(v) => patchOpts({ mapLow: v })} />
            <ColorField testid="map-mid" value={opts.mapMid} fallback="#2B4EFF" onChange={(v) => patchOpts({ mapMid: v })} />
            <ColorField testid="map-high" value={opts.mapHigh} fallback="#101E66" onChange={(v) => patchOpts({ mapHigh: v })} />
          </div>
        </div>
        <Row label="No-data color"><ColorField testid="map-nodata-color" value={opts.mapNoDataColor} fallback="#E7EBF5" onChange={(v) => patchOpts({ mapNoDataColor: v })} /></Row>
        <Row label="Border">
          <span className="flex items-center gap-1.5">
            <ColorField testid="map-border-color" value={opts.mapBorderColor} fallback="#ffffff" onChange={(v) => patchOpts({ mapBorderColor: v })} />
            <NumInput testid="map-border-width" value={opts.mapBorderWidth} placeholder="0.5" onChange={(v) => patchOpts({ mapBorderWidth: v })} className="w-14" />
          </span>
        </Row>
        <Row label="Legend"><Sel testid="map-legend-pos" value={opts.mapLegendPos || "bottom_left"} onChange={(v) => patchOpts({ mapLegendPos: v })} items={[["bottom_left", "Bottom left"], ["top_left", "Top left"], ["bottom", "Bottom"], ["top", "Top"], ["right", "Right"], ["hidden", "Hidden"]]} /></Row>
        <Row label="Legend type"><Sel testid="map-legend-type" value={opts.mapLegendType || "continuous"} onChange={(v) => patchOpts({ mapLegendType: v })} items={[["continuous", "Gradient bar"], ["piecewise", "Stepped ranges"]]} /></Row>
        {opts.mapLegendType === "piecewise" && <Row label="Steps"><NumInput testid="map-steps" value={opts.mapSteps} placeholder="5" onChange={(v) => patchOpts({ mapSteps: v })} className="w-[70px]" /></Row>}
        <Row label="Legend title"><Input data-testid="map-legend-title" value={opts.mapLegendTitle || ""} placeholder="(none)" onChange={(e) => patchOpts({ mapLegendTitle: e.target.value })} className="bg-[color:var(--surface)] w-[130px] h-7 text-xs" /></Row>
        <Row label="Region / bubble labels"><Switch data-testid="map-show-labels" checked={!!opts.mapShowLabels} onCheckedChange={(v) => patchOpts({ mapShowLabels: v })} /></Row>
        {opts.mapShowLabels && (
          <>
            <Row label="Label content"><Sel testid="map-label-mode" value={opts.mapLabelMode || "name"} onChange={(v) => patchOpts({ mapLabelMode: v })} items={[["name", "Name"], ["value", "Value"], ["name_value", "Name + value"]]} /></Row>
            <Row label="Label style">
              <span className="flex items-center gap-1.5">
                <ColorField testid="map-label-color" value={opts.mapLabelColor} fallback="#0A1029" onChange={(v) => patchOpts({ mapLabelColor: v })} />
                <NumInput testid="map-label-size" value={opts.mapLabelSize} placeholder="10" onChange={(v) => patchOpts({ mapLabelSize: v })} className="w-12" />
              </span>
            </Row>
          </>
        )}
        {widget?.type === "bubble_map" && (
          <>
            <div className="grid grid-cols-2 gap-1.5">
              <NumInput testid="map-bubble-min" value={opts.bubbleMin} placeholder="Min size (8)" onChange={(v) => patchOpts({ bubbleMin: v })} />
              <NumInput testid="map-bubble-max" value={opts.bubbleMax} placeholder="Max size (40)" onChange={(v) => patchOpts({ bubbleMax: v })} />
            </div>
            <Row label="Bubble color"><Sel testid="map-bubble-color-mode" value={opts.bubbleColorMode || "auto"} onChange={(v) => patchOpts({ bubbleColorMode: v === "auto" ? "" : v })} items={[["auto", "Auto"], ["single", "Single color"], ["category", "Each location"], ["series", "2nd dimension"], ["value", "Value gradient"]]} /></Row>
            {(opts.bubbleColorMode || "") === "single" && <Row label="Fill"><ColorField testid="map-bubble-color" value={opts.markerColor} fallback="#2B4EFF" onChange={(v) => patchOpts({ markerColor: v })} /></Row>}
            <SliderRow label={`Bubble opacity (${Math.round((opts.bubbleOpacity ?? 0.72) * 100)}%)`} min={10} max={100} step={5} value={Math.round((opts.bubbleOpacity ?? 0.72) * 100)} onChange={(v) => patchOpts({ bubbleOpacity: v / 100 })} testid="map-bubble-opacity" />
          </>
        )}
      </div>
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5" data-testid="map-city-editor">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">City / point drill level</Label>
        <p className="text-[10px] text-[color:var(--ink-3)]">When a drill level has no shape map (e.g. state → city), locations are plotted as points on the parent map. Pick lat/lon columns when the dataset has them; otherwise the built-in city lookup is used.</p>
        {[["lat", "Latitude field", "map-city-lat"], ["lon", "Longitude field", "map-city-lon"]].map(([k, lbl, tid]) => (
          <div key={k}>
            <Label className="text-[11px] text-[color:var(--ink-3)]">{lbl}</Label>
            <Select value={(config?.geo || {})[k] || "__none__"} onValueChange={(v) => patchConfig && patchConfig({ geo: { ...(config?.geo || {}), [k]: v === "__none__" ? "" : v } })}>
              <SelectTrigger data-testid={tid} className="mt-1 w-full bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="None (built-in lookup)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None (built-in lookup)</SelectItem>
                {(numericFields || []).map((f) => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
        <Row label="Plot unmatched names as cities"><Switch data-testid="map-city-layer" checked={opts.mapCityLayer !== false} onCheckedChange={(v) => patchOpts({ mapCityLayer: v })} /></Row>
        <Row label="Show unmatched note"><Switch data-testid="map-unmatched-note" checked={opts.mapUnmatchedNote !== false} onCheckedChange={(v) => patchOpts({ mapUnmatchedNote: v })} /></Row>
      </div>
      <p className="text-[10px] text-[color:var(--ink-3)]">Scroll / pinch to zoom, drag to pan. For street / satellite maps use the Point Map widget.</p>
    </Card>
  );
}

// ---------- Items 125-128: point map fields + layers ----------
export function GeoFieldsEditor({ config, patchConfig, numericFields, allFields, disp }) {
  const geo = config.geo || {};
  const set = (patch) => {
    const g = { ...geo, ...patch };
    patchConfig({ geo: g, dimensions: geoQueryDims(g) });
  };
  const sel = (testid, value, onChange, items, placeholder, allowNone) => (
    <Select value={value || (allowNone ? "__none__" : "")} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="mt-1 w-full bg-[color:var(--surface)] h-8 text-xs"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value="__none__">None</SelectItem>}
        {items.map((c) => <SelectItem key={c.name} value={c.name}>{disp ? disp(c.name) : c.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  return (
    <div data-testid="geo-fields-editor" className="space-y-2 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />Geographic fields</Label>
        <Button data-testid="geo-auto-detect" size="sm" variant="outline" className="h-6 text-[10px] px-2"
          onClick={() => {
            const det = detectGeoFields([...(numericFields || []), ...(allFields || [])]);
            const patch = {};
            if (det.lat) patch.lat = det.lat;
            if (det.lng) patch.lon = det.lng;
            if (det.city || det.state || det.country) patch.label = det.city || det.state || det.country;
            if (Object.keys(patch).length) set(patch);
          }}>
          Auto-detect
        </Button>
      </div>
      <div><Label className="text-[11px] text-[color:var(--ink-3)]">Latitude</Label>{sel("geo-lat-field", geo.lat, (v) => set({ lat: v }), numericFields, "Pick latitude field")}</div>
      <div><Label className="text-[11px] text-[color:var(--ink-3)]">Longitude</Label>{sel("geo-lon-field", geo.lon, (v) => set({ lon: v }), numericFields, "Pick longitude field")}</div>
      <div><Label className="text-[11px] text-[color:var(--ink-3)]">Label (city / place — optional)</Label>{sel("geo-label-field", geo.label, (v) => set({ label: v }), allFields, "None", true)}</div>
      <div><Label className="text-[11px] text-[color:var(--ink-3)]">Color by category (optional)</Label>{sel("geo-color-field", geo.colorField, (v) => set({ colorField: v }), allFields, "None", true)}</div>
      <p className="text-[10px] text-[color:var(--ink-3)]">Add a measure to size bubbles by value. Invalid coordinates are skipped safely.</p>
    </div>
  );
}

// Every field the point map needs must be part of the semantic query's dimensions.
export function geoQueryDims(g) {
  const fields = [g.label, g.lat, g.lon, g.colorField, g.routeSort, g.territoryField, ...(g.drillFields || [])].filter(Boolean);
  return [...new Set(fields)].map((f) => ({ field: f, date_part: "none" }));
}

export function PointMapStyleEditor({ config, patchConfig, dims = [] }) {
  const geo = config.geo || {};
  const set = (patch) => {
    const g = { ...geo, ...patch };
    patchConfig({ geo: g, dimensions: geoQueryDims(g) });
  };
  const mode = geo.mode || "markers";
  const allFieldNames = [...new Set([...(config.dimensions || []).map((d) => d.field), geo.label, geo.colorField].filter(Boolean))];
  return (
    <Card icon={MapIcon} title="Map layer & behavior" testid="pointmapstyle-editor">
      <Row label="Visualization">
        <Select value={mode} onValueChange={(v) => set({ mode: v === "markers" ? null : v })}>
          <SelectTrigger data-testid="pointmap-mode" className="bg-[color:var(--surface)] w-[160px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="markers">Markers / bubbles</SelectItem>
            <SelectItem value="heatmap">Heat map</SelectItem>
            <SelectItem value="route">Route (connect points)</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      {mode === "heatmap" && (
        <>
          <Row label={`Heat radius (${geo.heatRadius || 28}px)`}>
            <div className="w-[110px]"><Slider data-testid="pointmap-heat-radius" min={10} max={60} step={2} value={[geo.heatRadius || 28]} onValueChange={([v]) => set({ heatRadius: v })} /></div>
          </Row>
          <Row label={`Heat blur (${geo.heatBlur || 18}px)`}>
            <div className="w-[110px]"><Slider data-testid="pointmap-heat-blur" min={5} max={40} step={1} value={[geo.heatBlur || 18]} onValueChange={([v]) => set({ heatBlur: v })} /></div>
          </Row>
        </>
      )}
      {mode === "route" && (
        <>
          <div>
            <Label className="text-[11px] text-[color:var(--ink-3)]">Order stops by (optional)</Label>
            <Select value={geo.routeSort || "__none__"} onValueChange={(v) => set({ routeSort: v === "__none__" ? null : v })}>
              <SelectTrigger data-testid="pointmap-route-sort" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Data order" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Data order</SelectItem>
                {allFieldNames.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Row label="Route color"><ColorField testid="pointmap-route-color" value={geo.routeColor} fallback="#2B4EFF" onChange={(v) => set({ routeColor: v })} /></Row>
          <Row label={`Route width (${geo.routeWidth || 3}px)`}>
            <div className="w-[110px]"><Slider data-testid="pointmap-route-width" min={1} max={8} step={0.5} value={[geo.routeWidth || 3]} onValueChange={([v]) => set({ routeWidth: v })} /></div>
          </Row>
          <Row label="Dashed line"><Switch data-testid="pointmap-route-dash" checked={!!geo.routeDash} onCheckedChange={(v) => set({ routeDash: v })} /></Row>
        </>
      )}
      <div className="pt-1 border-t border-[color:var(--glass-border)]">
        <Label className="text-[11px] text-[color:var(--ink-3)]">Territory layer — GeoJSON URL (optional)</Label>
        <Input data-testid="pointmap-geojson-url" value={geo.geojsonUrl || ""} onChange={(e) => set({ geojsonUrl: e.target.value })} placeholder="https://…/territories.geojson" className="mt-1 bg-[color:var(--surface)] h-7 text-xs" />
        {geo.geojsonUrl && (
          <>
            <div className="grid grid-cols-2 gap-1.5 mt-1.5">
              <div>
                <Label className="text-[10px] text-[color:var(--ink-3)]">Feature name property</Label>
                <Input data-testid="pointmap-geojson-prop" value={geo.geojsonProp || ""} onChange={(e) => set({ geojsonProp: e.target.value })} placeholder="name" className="mt-0.5 bg-[color:var(--surface)] h-7 text-xs" />
              </div>
              <div>
                <Label className="text-[10px] text-[color:var(--ink-3)]">Match data field</Label>
                <Select value={geo.territoryField || "__none__"} onValueChange={(v) => set({ territoryField: v === "__none__" ? null : v })}>
                  <SelectTrigger data-testid="pointmap-territory-field" className="mt-0.5 bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Label field" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Label field</SelectItem>
                    {allFieldNames.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Row label="Territory color"><ColorField testid="pointmap-territory-color" value={geo.territoryColor} fallback="#2B4EFF" onChange={(v) => set({ territoryColor: v })} /></Row>
            <p className="text-[9px] text-[color:var(--ink-3)] mt-1">Regions are shaded by the first measure's total where the feature property matches your data. Tip: hold Shift and drag on the map to select multiple points.</p>
          </>
        )}
      </div>
      <Row label="Map type">
        <Select value={geo.tile || "osm"} onValueChange={(v) => set({ tile: v })}>
          <SelectTrigger data-testid="pointmap-tile" className="bg-[color:var(--surface)] w-[160px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{TILE_KEYS.map((k) => <SelectItem key={k} value={k}>{TILE_PRESETS[k].label}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      {geo.tile === "custom" && (
        <Input data-testid="pointmap-custom-url" value={geo.customTileUrl || ""} onChange={(e) => set({ customTileUrl: e.target.value })} placeholder="https://{s}.tile.example.com/{z}/{x}/{y}.png" className="bg-[color:var(--surface)] h-7 text-xs" />
      )}
      <Row label="Cluster nearby points"><Switch data-testid="pointmap-cluster" checked={geo.cluster !== false} onCheckedChange={(v) => set({ cluster: v })} /></Row>
      {geo.cluster !== false && (
        <Row label={`Cluster radius (${geo.clusterRadius || 44}px)`}>
          <div className="w-[110px]"><Slider data-testid="pointmap-cluster-radius" min={20} max={120} step={4} value={[geo.clusterRadius || 44]} onValueChange={([v]) => set({ clusterRadius: v })} /></div>
        </Row>
      )}
      <Row label="Auto zoom to data"><Switch data-testid="pointmap-autozoom" checked={geo.autoZoom !== false} onCheckedChange={(v) => set({ autoZoom: v })} /></Row>
      <Row label="Zoom controls"><Switch data-testid="pointmap-zoomcontrols" checked={geo.zoomControls !== false} onCheckedChange={(v) => set({ zoomControls: v })} /></Row>
      <Row label="Search / reset / fullscreen"><Switch data-testid="pointmap-mapcontrols" checked={geo.mapControls !== false} onCheckedChange={(v) => set({ mapControls: v })} /></Row>
      <Row label="Bubble sizing">
        <Select value={geo.sizeMode || (geo.sizeByMeasure === false ? "fixed" : "measure")} onValueChange={(v) => set({ sizeMode: v, sizeByMeasure: v !== "fixed" })}>
          <SelectTrigger data-testid="pointmap-size-mode" className="bg-[color:var(--surface)] w-[160px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="measure">By measure (proportional)</SelectItem>
            <SelectItem value="fixed">Fixed size</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      {(geo.sizeMode || (geo.sizeByMeasure === false ? "fixed" : "measure")) === "fixed" ? (
        <Row label={`Fixed size (${geo.fixedSize || 8}px)`}>
          <div className="w-[110px]"><Slider data-testid="pointmap-fixed-size" min={2} max={30} step={1} value={[geo.fixedSize || 8]} onValueChange={([v]) => set({ fixedSize: v })} /></div>
        </Row>
      ) : (
        <div className="flex gap-1.5 items-center">
          <Label className="text-[10px] text-[color:var(--ink-3)] shrink-0">Size range</Label>
          <NumInput testid="pointmap-min-size" value={geo.minSize} placeholder="6" onChange={(v) => set({ minSize: v })} className="w-[64px]" />
          <span className="text-[10px] text-[color:var(--ink-3)]">–</span>
          <NumInput testid="pointmap-max-size" value={geo.maxSize} placeholder="22" onChange={(v) => set({ maxSize: v })} className="w-[64px]" />
          <span className="text-[10px] text-[color:var(--ink-3)]">px</span>
        </div>
      )}
      <Row label="Hover tooltip"><Switch data-testid="pointmap-hover-tooltip" checked={geo.hoverTooltip !== false} onCheckedChange={(v) => set({ hoverTooltip: v })} /></Row>
      <Row label="Marker color"><ColorField testid="pointmap-color" value={geo.color} fallback="#2B4EFF" onChange={(v) => set({ color: v })} /></Row>
      <Row label="Border color"><ColorField testid="pointmap-border-color" value={geo.borderColor} fallback="#ffffff" onChange={(v) => set({ borderColor: v })} /></Row>
      <Row label={`Border width (${geo.borderWidth ?? 1.5}px)`}>
        <div className="w-[110px]"><Slider data-testid="pointmap-border-width" min={0} max={5} step={0.5} value={[geo.borderWidth ?? 1.5]} onValueChange={([v]) => set({ borderWidth: v })} /></div>
      </Row>
      <Row label={`Marker opacity (${Math.round((typeof geo.opacity === "number" ? geo.opacity : 0.78) * 100)}%)`}>
        <div className="w-[110px]"><Slider data-testid="pointmap-opacity" min={20} max={100} step={5} value={[Math.round((typeof geo.opacity === "number" ? geo.opacity : 0.78) * 100)]} onValueChange={([v]) => set({ opacity: v / 100 })} /></div>
      </Row>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Custom marker icon (URL — replaces bubbles)</Label>
        <div className="mt-1"><MediaInput testid="pointmap-icon-url" value={geo.iconUrl || ""} onChange={(v) => set({ iconUrl: v })} placeholder="https://.../pin.png" /></div>
      </div>
      {geo.iconUrl && (
        <Row label={`Icon size (${geo.iconSize || 28}px)`}>
          <div className="w-[110px]"><Slider data-testid="pointmap-icon-size" min={14} max={64} step={2} value={[geo.iconSize || 28]} onValueChange={([v]) => set({ iconSize: v })} /></div>
        </Row>
      )}
      <Row label="Category legend"><Switch data-testid="pointmap-legend-toggle" checked={geo.showLegend !== false} onCheckedChange={(v) => set({ showLegend: v })} /></Row>
      {geo.showLegend !== false && (
        <>
          <Row label="Legend position">
            <Select value={geo.legendPos || "br"} onValueChange={(v) => set({ legendPos: v })}>
              <SelectTrigger data-testid="pointmap-legend-pos" className="bg-[color:var(--surface)] w-[160px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="br">Bottom right</SelectItem>
                <SelectItem value="bl">Bottom left</SelectItem>
                <SelectItem value="tr">Top right</SelectItem>
                <SelectItem value="tl">Top left</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <Row label="Legend title">
            <Input data-testid="pointmap-legend-title" value={geo.legendTitle || ""} onChange={(e) => set({ legendTitle: e.target.value })} placeholder="(none)" className="bg-[color:var(--surface)] w-[160px] h-7 text-xs" />
          </Row>
        </>
      )}
      <div className="pt-1 border-t border-[color:var(--glass-border)]">
        <Row label="Data labels"><Switch data-testid="pointmap-labels-toggle" checked={!!(geo.labels || {}).show} onCheckedChange={(v) => set({ labels: { ...(geo.labels || {}), show: v } })} /></Row>
        {(geo.labels || {}).show && (
          <>
            <div>
              <Label className="text-[10px] text-[color:var(--ink-3)]">Label field</Label>
              <Select value={(geo.labels || {}).field || "__auto__"} onValueChange={(v) => set({ labels: { ...(geo.labels || {}), field: v === "__auto__" ? null : v } })}>
                <SelectTrigger data-testid="pointmap-label-field" className="mt-0.5 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">Auto (point label)</SelectItem>
                  {allFieldNames.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  {(config.measures || []).map((m) => {
                    const k = m.key || m.alias || `${m.agg || "sum"}_${m.field}`;
                    return <SelectItem key={k} value={k}>{m.label || k} (measure)</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <Row label={`Label size (${(geo.labels || {}).fontSize || 10}px)`}>
              <div className="w-[110px]"><Slider data-testid="pointmap-label-size" min={7} max={18} step={1} value={[(geo.labels || {}).fontSize || 10]} onValueChange={([v]) => set({ labels: { ...(geo.labels || {}), fontSize: v } })} /></div>
            </Row>
            <Row label="Label color"><ColorField testid="pointmap-label-color" value={(geo.labels || {}).color} fallback="#334155" onChange={(v) => set({ labels: { ...(geo.labels || {}), color: v } })} /></Row>
            <Row label="Label position">
              <Select value={(geo.labels || {}).position || "below"} onValueChange={(v) => set({ labels: { ...(geo.labels || {}), position: v } })}>
                <SelectTrigger data-testid="pointmap-label-pos" className="bg-[color:var(--surface)] w-[160px] h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="below">Below point</SelectItem>
                  <SelectItem value="above">Above point</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <p className="text-[9px] text-[color:var(--ink-3)]">Labels use the measure's number format; hidden above 300 points to keep the map readable.</p>
          </>
        )}
      </div>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Drill hierarchy (e.g. Country → State → City)</Label>
        <div className="mt-1 space-y-1">
          {(geo.drillFields || []).map((f, i) => (
            <div key={i} className="flex gap-1 items-center">
              <Select value={f || ""} onValueChange={(v) => set({ drillFields: (geo.drillFields || []).map((x, j) => (j === i ? v : x)) })}>
                <SelectTrigger data-testid={`pointmap-drill-${i}`} className="bg-[color:var(--surface)] flex-1 h-7 text-xs"><SelectValue placeholder="Field" /></SelectTrigger>
                <SelectContent>{(dims || []).map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => set({ drillFields: (geo.drillFields || []).filter((_, j) => j !== i) })} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <Button data-testid="pointmap-drill-add" size="sm" variant="outline" className="w-full h-6 text-[10px]" onClick={() => set({ drillFields: [...(geo.drillFields || []), ""] })}><Plus className="w-3 h-3 mr-1" />Add drill level</Button>
        </div>
      </div>
      <p className="text-[10px] text-[color:var(--ink-3)]">Tooltips show every configured measure with its number format. Range colors (Conditional Formatting) recolor bubbles by value. Clicking a point cross-filters other widgets.</p>
    </Card>
  );
}

// ---------- Item 112: one-click Year → Quarter → Month → Day hierarchy ----------
export function DateHierarchyButton({ config, patchConfig, isDateDim }) {
  if (!isDateDim) return null;
  const f = config.dimensions?.[0]?.field;
  const apply = () => patchConfig({
    dimensions: [{ ...config.dimensions[0], date_part: "year" }, ...(config.dimensions || []).slice(1)],
    drilldownPath: [
      { field: f, date_part: "quarter" },
      { field: f, date_part: "month" },
      { field: f, date_part: "day" },
    ],
  });
  return (
    <Button data-testid="apply-date-hierarchy" size="sm" variant="outline" className="w-full h-7 text-xs text-[color:var(--brand-ocean)] border-[color:rgba(43,78,255,0.3)]" onClick={apply}>
      Use date hierarchy (Year → Quarter → Month → Day)
    </Button>
  );
}

// Item 88: Google Map widget options (mode, map type, theme, layers, route, heat).
export function GoogleMapStyleEditor({ config, patchConfig }) {
  const geo = config.geo || {};
  const set = (p) => patchConfig({ geo: { ...geo, ...p } });
  return (
    <div data-testid="gmap-style-editor" className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <Label className="text-xs text-[color:var(--ink-2)] font-medium">Google Map</Label>
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Visualization</Label>
          <Select value={geo.gMode || "markers"} onValueChange={(v) => set({ gMode: v })}>
            <SelectTrigger data-testid="gmap-mode" className="mt-0.5 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="markers">Markers / Bubbles</SelectItem>
              <SelectItem value="heat">Heat map</SelectItem>
              <SelectItem value="route">Route (ordered line)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Map type</Label>
          <Select value={geo.gMapType || "roadmap"} onValueChange={(v) => set({ gMapType: v })}>
            <SelectTrigger data-testid="gmap-type" className="mt-0.5 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="roadmap">Roadmap</SelectItem>
              <SelectItem value="satellite">Satellite</SelectItem>
              <SelectItem value="terrain">Terrain</SelectItem>
              <SelectItem value="hybrid">Hybrid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Theme</Label>
          <Select value={geo.gTheme || "standard"} onValueChange={(v) => set({ gTheme: v === "standard" ? "" : v })}>
            <SelectTrigger data-testid="gmap-theme" className="mt-0.5 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
              <SelectItem value="night">Night</SelectItem>
              <SelectItem value="silver">Silver</SelectItem>
              <SelectItem value="retro">Retro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {geo.gMode === "heat" && (
          <div>
            <Label className="text-[10px] text-[color:var(--ink-3)]">Heat radius (px)</Label>
            <Input data-testid="gmap-heat-radius" type="number" min={4} max={80} value={geo.heatRadius ?? 24}
              onChange={(e) => set({ heatRadius: Number(e.target.value) || 24 })} className="mt-0.5 bg-[color:var(--surface)] h-7 text-xs" />
          </div>
        )}
        {geo.gMode === "route" && (
          <div className="flex gap-1 items-end">
            <input data-testid="gmap-route-color" type="color" title="Route color" value={geo.routeColor || "#2B4EFF"}
              onChange={(e) => set({ routeColor: e.target.value })} className="h-7 w-9 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
            <Input data-testid="gmap-route-width" type="number" min={1} max={12} value={geo.routeWidth ?? 3}
              onChange={(e) => set({ routeWidth: Number(e.target.value) || 3 })} className="bg-[color:var(--surface)] h-7 text-xs w-[64px]" placeholder="w" />
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 flex-wrap pt-0.5">
        <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]">
          <Switch data-testid="gmap-cluster" checked={!!geo.cluster} onCheckedChange={(v) => set({ cluster: v })} />Cluster
        </label>
        <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]">
          <Switch data-testid="gmap-traffic" checked={!!geo.traffic} onCheckedChange={(v) => set({ traffic: v })} />Traffic
        </label>
        <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]">
          <Switch data-testid="gmap-transit" checked={!!geo.transit} onCheckedChange={(v) => set({ transit: v })} />Transit
        </label>
        <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]">
          <Switch data-testid="gmap-legend-toggle" checked={geo.showLegend !== false} onCheckedChange={(v) => set({ showLegend: v })} />Legend
        </label>
      </div>
      <p className="text-[9px] text-[color:var(--ink-3)]">Marker size/color, bubble scaling, tooltip fields and advanced colors are shared with the Point Map settings above.</p>
    </div>
  );
}
